import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, euro, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { InternalLayout, PageHeader } from "@/components/InternalLayout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MultiSelect } from "@/components/MultiSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, Area, ComposedChart, Line, LabelList, PieChart, Pie, Cell } from "recharts";
import { LifebuoyIcon as Lifebuoy, ClockIcon as Clock, DownloadSimpleIcon as DownloadSimple, CaretUpIcon as CaretUp, CaretDownIcon as CaretDown, MagnifyingGlassIcon as MagnifyingGlass, WarningCircleIcon as WarningCircle, TrendUpIcon as TrendUp, ChartLineUpIcon as ChartLineUp, ArrowUpIcon as ArrowUp, ArrowDownIcon as ArrowDown, MinusIcon as Minus, ArrowsLeftRightIcon as ArrowsLeftRight, PencilSimpleIcon as PencilSimple, ArrowCounterClockwiseIcon as ArrowCounterClockwise, FunnelIcon as Funnel, CurrencyEurIcon as CurrencyEur, UsersIcon as Users, IdentificationCardIcon as IdentificationCard, ChartBarIcon as ChartBar, TargetIcon as Target, StarIcon as Star, SmileyIcon as Smiley } from "@phosphor-icons/react";

const GRID = "#E4E4E7";
const tip = { contentStyle: { background: "#09090B", border: "none", borderRadius: 4, color: "#fff", fontSize: 12 } };

const monthDefaults = () => {
  const n = new Date();
  const p = (x) => String(x).padStart(2, "0");
  const first = `${n.getFullYear()}-${p(n.getMonth() + 1)}-01`;
  const last = new Date(n.getFullYear(), n.getMonth() + 1, 0);
  return { date_from: first, date_to: `${last.getFullYear()}-${p(last.getMonth() + 1)}-${p(last.getDate())}` };
};

const fmtOre = (v) => (v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const eurK = (v) => (Math.abs(v) >= 1000 ? `${(v / 1000).toLocaleString("it-IT", { maximumFractionDigits: 1 })}k` : (v || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 }));
const fmtD = (s) => { if (!s) return "—"; const [y, m, d] = s.split("-"); return `${d}/${m}/${y}`; };

// Persistenza preferenze filtri per utente (localStorage, come i ticket)
const REPORT_PREFS_PREFIX = "reportistica_ore_prefs_";
const loadReportPrefs = (key) => { try { return JSON.parse(localStorage.getItem(key)) || {}; } catch { return {}; } };

// Filtri reportistica a selezione multipla (array). Serializzati come CSV per il backend (retrocompatibile).
const MULTI_KEYS = ["tecnico_id", "cliente_id", "stato", "modalita_addebito", "contratto_id", "inviata"];
const emptyMulti = () => ({ tecnico_id: [], cliente_id: [], stato: [], modalita_addebito: [], contratto_id: [], inviata: [] });
const serializeFilters = (f) => {
  const out = {};
  for (const [k, v] of Object.entries(f)) {
    if (Array.isArray(v)) { if (v.length) out[k] = v.join(","); }
    else if (v !== "" && v != null) out[k] = v;
  }
  return out;
};
const normalizeMulti = (f) => {
  const out = { ...f };
  for (const k of MULTI_KEYS) {
    const v = out[k];
    if (Array.isArray(v)) continue;
    out[k] = v === "" || v == null ? [] : [v];
  }
  return out;
};


const STATO_COLOR = {
  completata: "bg-emerald-100 text-emerald-700", in_corso: "bg-violet-100 text-violet-700",
  pianificata: "bg-blue-100 text-blue-700", da_pianificare: "bg-amber-100 text-amber-700",
  sospesa: "bg-orange-100 text-orange-700", annullata: "bg-slate-100 text-slate-600",
  in_attesa_conferma: "bg-yellow-100 text-yellow-700",
};

const CONF_CARDS = [
  { k: "totale_ore", l: "Ore totali", fmt: "ore" },
  { k: "totale_ore_rendicontate", l: "Ore rendicontate", fmt: "ore" },
  { k: "totale_ore_escluse", l: "Ore escluse", fmt: "ore" },
  { k: "totale_importo_stimato", l: "Importo stimato", fmt: "eur" },
  { k: "numero_rapportini", l: "Rapportini", fmt: "int" },
  { k: "numero_clienti", l: "Clienti", fmt: "int" },
  { k: "numero_tecnici", l: "Tecnici", fmt: "int" },
  { k: "media_euro_ora", l: "Media €/ora", fmt: "eur" },
];
const fmtConf = (v, f) => (f === "ore" ? `${fmtOre(v)} h` : f === "eur" ? euro(v || 0) : String(v || 0));
const fmtDelta = (v, f) => { const s = v > 0 ? "+" : ""; return f === "ore" ? `${s}${fmtOre(v)} h` : f === "eur" ? `${s}${euro(v || 0)}` : `${s}${v}`; };
const fmtPct = (p) => (p == null ? "Nuovo valore" : `${p > 0 ? "+" : ""}${p.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`);

function ConfrontoCard({ card, cur, prev, varz }) {
  const c = cur?.[card.k] ?? 0, p = prev?.[card.k] ?? 0;
  const delta = varz?.delta ?? 0, pct = varz?.delta_percentuale;
  const up = delta > 0, down = delta < 0;
  const Icon = up ? ArrowUp : down ? ArrowDown : Minus;
  const color = pct == null ? "text-blue-600" : up ? "text-emerald-600" : down ? "text-rose-600" : "text-muted-foreground";
  return (
    <Card className="rounded-sm border-border shadow-none p-3" data-testid={`conf-${card.k}`}>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{card.l}</div>
      <div className="font-heading text-lg font-bold mt-1 tabular-nums">{fmtConf(c, card.fmt)}</div>
      <div className="text-[11px] text-muted-foreground">prec. {fmtConf(p, card.fmt)}</div>
      <div className={`flex items-center gap-1 mt-1.5 text-[11px] font-medium ${color}`}>
        <Icon size={12} weight="bold" />
        <span data-testid={`conf-pct-${card.k}`}>{fmtPct(pct)}</span>
        {pct != null && <span className="text-muted-foreground font-normal">({fmtDelta(delta, card.fmt)})</span>}
      </div>
    </Card>
  );
}

function ConfrontoPeriodo({ data }) {
  const cp = data?.confronto_periodo;
  if (!cp) return null;
  const cur = cp.periodo_corrente.kpi, prev = cp.periodo_precedente.kpi, varz = cp.variazioni || {};
  const chartOre = [
    { name: "Ore totali", Corrente: cur.totale_ore, Precedente: prev.totale_ore },
    { name: "Rendicontate", Corrente: cur.totale_ore_rendicontate, Precedente: prev.totale_ore_rendicontate },
    { name: "Rapportini", Corrente: cur.numero_rapportini, Precedente: prev.numero_rapportini },
  ];
  const chartImp = [{ name: "Importo stimato", Corrente: cur.totale_importo_stimato, Precedente: prev.totale_importo_stimato }];
  return (
    <Card className="rounded-sm border-border shadow-none p-4" data-testid="confronto-periodo">
      <div className="mb-3">
        <h3 className="font-heading text-sm font-semibold flex items-center gap-1.5"><ArrowsLeftRight size={16} /> Confronto periodo</h3>
        <p className="text-xs text-muted-foreground">Variazione rispetto al periodo precedente equivalente · <span className="font-medium text-foreground/70">{cp.periodo_corrente.label}</span> vs <span className="font-medium text-foreground/70">{cp.periodo_precedente.label}</span></p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {CONF_CARDS.map((card) => <ConfrontoCard key={card.k} card={card} cur={cur} prev={prev} varz={varz[card.k]} />)}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartOre} margin={{ top: 5, left: -12 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
              <Tooltip {...tip} formatter={(v) => fmtOre(v)} /><Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Corrente" fill="#00396a" radius={[2, 2, 0, 0]} />
              <Bar dataKey="Precedente" fill="#94a3b8" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartImp} margin={{ top: 5, left: -8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 10 }} />
              <Tooltip {...tip} formatter={(v) => euro(v || 0)} /><Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Corrente" fill="#0b8457" radius={[2, 2, 0, 0]} />
              <Bar dataKey="Precedente" fill="#94a3b8" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </Card>
  );
}

function AndamentoTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-sm bg-[#09090B] text-white text-[11px] p-2.5 shadow-lg min-w-[160px]">
      <div className="font-semibold mb-1">{p.label}</div>
      <div className="flex justify-between gap-4"><span className="text-white/60">Ore totali</span><span className="tabular-nums">{fmtOre(p.ore_totali)}</span></div>
      <div className="flex justify-between gap-4"><span className="text-emerald-400">Rendicontate</span><span className="tabular-nums">{fmtOre(p.ore_rendicontate)}</span></div>
      <div className="flex justify-between gap-4"><span className="text-white/40">Escluse</span><span className="tabular-nums">{fmtOre(p.ore_escluse)}</span></div>
      <div className="flex justify-between gap-4 mt-1 pt-1 border-t border-white/15"><span className="text-white/60">Importo stimato</span><span className="tabular-nums">{euro(p.importo_stimato || 0)}</span></div>
    </div>
  );
}

const TEC_COLORS = ["#00396a", "#0b8457", "#d97706", "#7c3aed", "#dc2626", "#0891b2", "#db2777", "#65a30d", "#ea580c", "#2563eb", "#059669", "#9333ea"];

function AndamentoOre({ data, activeGran, onGran, onTecnico, activeTecnicoName }) {
  const arr = data?.charts?.andamento_ore || [];
  const tecArr = data?.charts?.andamento_tecnici || [];
  const tecNomi = data?.charts?.andamento_tecnici_nomi || [];
  const gran = activeGran || data?.charts?.andamento_granularita || "giorno";
  const GRAN_OPTS = [{ v: "giorno", l: "Giornaliero" }, { v: "settimana", l: "Settimanale" }, { v: "mese", l: "Mensile" }];
  const short = (l) => (gran === "giorno" ? (l || "").slice(0, 5) : (l || "").replace("Settimana ", "S").replace(" / ", "/"));
  const chartData = tecArr.map((p) => ({ ...p, _x: short(p.label) }));

  return (
    <Card className="rounded-xl border-slate-100 shadow-[0_2px_8px_-2px_rgba(0,57,106,0.08)] p-4" data-testid="andamento-ore">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-heading text-sm font-semibold flex items-center gap-1.5"><ChartLineUp size={16} className="text-primary" /> Andamento fatturato per tecnico</h3>
          <p className="text-xs text-muted-foreground">Fatturato per tecnico nel periodo — clicca un tecnico per filtrare la dashboard</p>
        </div>
        <div className="flex items-center gap-1 bg-secondary/60 rounded-sm p-0.5" data-testid="gran-selector">
          {GRAN_OPTS.map((o) => (
            <button key={o.v} onClick={() => onGran(o.v)}
              className={`px-2.5 py-1 rounded-sm text-xs font-medium transition-colors ${gran === o.v ? "bg-white shadow-sm text-primary" : "text-muted-foreground hover:text-foreground"}`}
              data-testid={`gran-${o.v}`}>{o.l}</button>
          ))}
        </div>
      </div>
      {tecArr.length === 0 ? <p className="text-xs text-muted-foreground py-10 text-center" data-testid="andamento-empty">Nessun dato nel periodo selezionato</p> : (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData} margin={{ top: 5, right: 5, left: -6 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
            <XAxis dataKey="_x" tick={{ fontSize: 10 }} interval={chartData.length > 20 ? Math.floor(chartData.length / 15) : 0} angle={chartData.length > 12 ? -35 : 0} textAnchor={chartData.length > 12 ? "end" : "middle"} height={chartData.length > 12 ? 48 : 24} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={eurK} label={{ value: "€", angle: -90, position: "insideLeft", style: { fontSize: 10, fill: "#94a3b8" } }} />
            <Tooltip {...tip} cursor={{ fill: "rgba(0,0,0,0.04)" }}
              formatter={(v, n, p) => {
                const ore = p?.payload?.__ore?.[n];
                return [`${euro(v || 0)}${ore != null ? ` · ${fmtOre(ore)} h` : ""}`, n];
              }} />
            <Legend verticalAlign="top" align="left" wrapperStyle={{ fontSize: 11, cursor: "pointer", paddingBottom: 8 }} onClick={(e) => onTecnico && onTecnico(e.dataKey || e.value)} />
            {tecNomi.map((tn, i) => (
              <Bar key={tn} dataKey={tn} name={tn} stackId="t" fill={TEC_COLORS[i % TEC_COLORS.length]}
                radius={i === tecNomi.length - 1 ? [2, 2, 0, 0] : [0, 0, 0, 0]}
                opacity={activeTecnicoName && activeTecnicoName !== tn ? 0.35 : 1}
                cursor="pointer" onClick={() => onTecnico && onTecnico(tn)} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}

// Rendimento Tecnici — Andamento ORE LAVORATE per tecnico (totali, incl. Garanzia/Comm./Costo copia)
function AndamentoOreLavorate({ data, activeGran, onGran, onTecnico, activeTecnicoName }) {
  const tecArr = data?.charts?.andamento_tecnici || [];
  const tecNomi = data?.charts?.andamento_tecnici_nomi || [];
  const gran = activeGran || data?.charts?.andamento_granularita || "giorno";
  const GRAN_OPTS = [{ v: "giorno", l: "Giornaliero" }, { v: "settimana", l: "Settimanale" }, { v: "mese", l: "Mensile" }];
  const short = (l) => (gran === "giorno" ? (l || "").slice(0, 5) : (l || "").replace("Settimana ", "S").replace(" / ", "/"));
  const chartData = tecArr.map((p) => ({ ...p, _x: short(p.label) }));

  return (
    <Card className="rounded-xl border-slate-100 shadow-[0_2px_8px_-2px_rgba(0,57,106,0.08)] p-4" data-testid="andamento-ore-lavorate">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-heading text-sm font-semibold flex items-center gap-1.5"><Clock size={16} className="text-primary" /> Andamento Ore Lavorate per tecnico</h3>
          <p className="text-xs text-muted-foreground">Ore lavorate per tecnico nel periodo — clicca un tecnico per filtrare la dashboard</p>
        </div>
        <div className="flex items-center gap-1 bg-secondary/60 rounded-sm p-0.5" data-testid="gran-selector-ore">
          {GRAN_OPTS.map((o) => (
            <button key={o.v} onClick={() => onGran(o.v)}
              className={`px-2.5 py-1 rounded-sm text-xs font-medium transition-colors ${gran === o.v ? "bg-white shadow-sm text-primary" : "text-muted-foreground hover:text-foreground"}`}
              data-testid={`gran-ore-${o.v}`}>{o.l}</button>
          ))}
        </div>
      </div>
      {tecArr.length === 0 ? <p className="text-xs text-muted-foreground py-10 text-center" data-testid="andamento-ore-empty">Nessun dato nel periodo selezionato</p> : (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData} margin={{ top: 5, right: 5, left: -6 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
            <XAxis dataKey="_x" tick={{ fontSize: 10 }} interval={chartData.length > 20 ? Math.floor(chartData.length / 15) : 0} angle={chartData.length > 12 ? -35 : 0} textAnchor={chartData.length > 12 ? "end" : "middle"} height={chartData.length > 12 ? 48 : 24} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmtOre(v)} label={{ value: "ore", angle: -90, position: "insideLeft", style: { fontSize: 10, fill: "#94a3b8" } }} />
            <Tooltip {...tip} cursor={{ fill: "rgba(0,0,0,0.04)" }}
              formatter={(v, n, p) => {
                const d = p?.payload?.__ore?.[n] || {};
                const extra = [];
                if (d.rend != null) extra.push(`rend. ${fmtOre(d.rend)}h`);
                if (d.nonval != null) extra.push(`non val. ${fmtOre(d.nonval)}h`);
                if (d.fatt != null) extra.push(euro(d.fatt || 0));
                return [`${fmtOre(v || 0)} h${extra.length ? ` · ${extra.join(" · ")}` : ""}`, n];
              }} />
            <Legend verticalAlign="top" align="left" wrapperStyle={{ fontSize: 11, cursor: "pointer", paddingBottom: 8 }} onClick={(e) => onTecnico && onTecnico(e.dataKey || e.value)} />
            {tecNomi.map((tn, i) => (
              <Bar key={tn} dataKey={tn} name={tn} stackId="t" fill={TEC_COLORS[i % TEC_COLORS.length]}
                radius={i === tecNomi.length - 1 ? [2, 2, 0, 0] : [0, 0, 0, 0]}
                opacity={activeTecnicoName && activeTecnicoName !== tn ? 0.35 : 1}
                cursor="pointer" onClick={() => onTecnico && onTecnico(tn)} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}


// Grafico Fatturato Mensile (anno della data iniziale del filtro) — linea con etichette colorate vs target
function FatturatoMensile({ data }) {
  const arr = (data?.fatturato_mensile || []).map((m) => ({ ...m, _x: m.label }));
  const hasTarget = arr.some((m) => (m.target || 0) > 0);
  const ValueLabel = (props) => {
    const { x, y, value, index } = props;
    if (value == null || x == null || y == null) return null;
    const m = arr[index] || {};
    const tgt = m.target || 0;
    const color = tgt > 0 ? (value >= tgt ? "#0b8457" : "#e11d48") : "#00396a";
    return <text x={x} y={y - 9} textAnchor="middle" fontSize={10} fontWeight={700} fill={color}>{eurK(value)}</text>;
  };
  return (
    <Card className="rounded-xl border-slate-100 shadow-[0_2px_8px_-2px_rgba(0,57,106,0.08)] p-4" data-testid="fatturato-mensile">
      <h3 className="font-heading text-sm font-semibold flex items-center gap-1.5 mb-3"><CurrencyEur size={16} className="text-primary" /> Fatturato Mensile</h3>
      {arr.length === 0 ? <p className="text-xs text-muted-foreground py-16 text-center">Nessun dato per l'anno selezionato</p> : (
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={arr} margin={{ top: 24, right: 12, left: 0 }}>
            <defs>
              <linearGradient id="fattFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#00396a" stopOpacity={0.22} />
                <stop offset="100%" stopColor="#00396a" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
            <XAxis dataKey="_x" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={eurK} />
            <Tooltip {...tip} formatter={(v, n) => [euro(v || 0), n === "fatturato" ? "Fatturato" : "Target mese"]} cursor={{ stroke: "#cbd5e1" }} />
            <Area type="monotone" dataKey="fatturato" name="Fatturato" stroke="#00396a" strokeWidth={2.5} fill="url(#fattFill)" dot={{ r: 3, fill: "#00396a" }} activeDot={{ r: 5 }}>
              <LabelList dataKey="fatturato" content={ValueLabel} />
            </Area>
            {hasTarget && <Line type="monotone" dataKey="target" name="Target mese" stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="5 4" dot={false} />}
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}

// Card media (giornaliera/oraria) con target configurato
function MediaCard({ label, value, target, onConfigure }) {
  const pct = target ? (value / target) * 100 : null;
  const color = pct == null ? "#0f172a" : pct >= 100 ? "#0b8457" : pct >= 90 ? "#d97706" : "#e11d48";
  return (
    <Card className="rounded-xl border-slate-100 shadow-[0_2px_8px_-2px_rgba(0,57,106,0.08)] p-3.5 flex flex-col" data-testid={`media-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</span>
        <CurrencyEur size={15} className="text-slate-300" weight="fill" />
      </div>
      <div className="font-heading text-2xl font-bold tabular-nums mt-1" style={{ color }}>{euro(value)}</div>
      {target != null ? (
        <span className="text-[10px] text-muted-foreground mt-0.5">Target {euro(target)} · <span style={{ color }}>{(pct || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 })}%</span></span>
      ) : (
        <button className="text-[10px] text-primary underline mt-0.5 text-left" onClick={onConfigure} data-testid="media-configura">Target non configurato</button>
      )}
    </Card>
  );
}


function KpiCard({ label, value, sub, accent, tip }) {
  return (
    <Card className="rounded-sm border-border shadow-none p-4" title={tip} data-testid={`kpi-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
      <div className={`font-heading text-2xl font-bold mt-1.5 tabular-nums ${accent || ""}`}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </Card>
  );
}

function ChartCard({ title, data, color, valueLabel = "ore", money = false }) {
  return (
    <Card className="rounded-sm border-border shadow-none p-4">
      <h3 className="font-heading text-sm font-semibold mb-3">{title}</h3>
      {data.length === 0 ? <p className="text-xs text-muted-foreground py-8 text-center">Nessun dato</p> : (
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={data} layout="vertical" margin={{ left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID} horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={money ? eurK : undefined} />
            <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 10 }} />
            <Tooltip formatter={(v) => [money ? euro(v || 0) : `${fmtOre(v)} ${valueLabel}`, ""]} {...tip} />
            <Bar dataKey="value" fill={color} radius={[0, 2, 2, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}

// ===== RENDIMENTO TECNICI =====
function RendTecnicoTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-md bg-[#09090B] text-white text-xs p-2.5 space-y-0.5 min-w-[190px]">
      <div className="font-semibold mb-1">{p.name}</div>
      <div className="flex justify-between gap-4"><span className="text-white/60">Fatturato</span><span>{euro(p.fatturato || 0)}</span></div>
      <div className="flex justify-between gap-4"><span className="text-white/60">Ore lavorate totali</span><span>{fmtOre(p.ore_lavorate_totali || 0)} h</span></div>
      <div className="flex justify-between gap-4"><span className="text-white/60">Ore rendicontate</span><span>{fmtOre(p.ore_rendicontate || 0)} h</span></div>
      <div className="flex justify-between gap-4"><span className="text-white/60">Ore non valorizzate</span><span>{fmtOre(p.ore_non_valorizzate || 0)} h</span></div>
      <div className="flex justify-between gap-4"><span className="text-white/60">Media €/h reale</span><span>{p.media_euro_ora_reale != null ? euro(p.media_euro_ora_reale) : "N/D"}</span></div>
      <div className="flex justify-between gap-4"><span className="text-white/60">Media €/h rendic.</span><span>{p.media_euro_ora_rendicontata != null ? euro(p.media_euro_ora_rendicontata) : "N/D"}</span></div>
    </div>
  );
}

function MediaOreTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-md bg-[#09090B] text-white text-xs p-2.5 space-y-0.5 min-w-[190px]">
      <div className="font-semibold mb-1">{p.name}</div>
      <div className="flex justify-between gap-4"><span className="text-white/60">Media ore fatturate</span><span>{fmtOre(p.value || 0)} h</span></div>
      <div className="flex justify-between gap-4"><span className="text-white/60">Ore lavorate totali</span><span>{fmtOre(p.ore_lavorate_totali || 0)} h</span></div>
      <div className="flex justify-between gap-4"><span className="text-white/60">Giorni con attività</span><span>{p.giorni_con_attivita || 0}</span></div>
      <div className="flex justify-between gap-4"><span className="text-white/60">Ore rendicontate</span><span>{fmtOre(p.ore_rendicontate || 0)} h</span></div>
      <div className="flex justify-between gap-4"><span className="text-white/60">Ore non valorizzate</span><span>{fmtOre(p.ore_non_valorizzate || 0)} h</span></div>
      <div className="flex justify-between gap-4"><span className="text-white/60">Fatturato</span><span>{euro(p.fatturato || 0)}</span></div>
    </div>
  );
}

function FatturatoPerTecnicoChart({ chart, onTecnico, activeTecnicoName }) {
  const data = chart || [];
  const h = Math.max(200, data.length * 40 + 30);
  return (
    <Card className="rounded-xl border-slate-100 shadow-[0_2px_8px_-2px_rgba(0,57,106,0.08)] p-4" data-testid="rend-fatturato-per-tecnico">
      <h3 className="font-heading text-sm font-semibold flex items-center gap-1.5 mb-1"><CurrencyEur size={16} className="text-primary" /> Fatturato prodotto per tecnico</h3>
      <p className="text-xs text-muted-foreground mb-3">Clicca un tecnico per filtrare la dashboard</p>
      {data.length === 0 ? <p className="text-xs text-muted-foreground py-10 text-center">Nessun dato nel periodo</p> : (
        <ResponsiveContainer width="100%" height={h}>
          <BarChart data={data} layout="vertical" margin={{ left: 10, right: 40 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID} horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={eurK} />
            <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 10 }} />
            <Tooltip content={<RendTecnicoTooltip />} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
            <Bar dataKey="fatturato" radius={[0, 3, 3, 0]} cursor="pointer" onClick={(_, i) => onTecnico && onTecnico(data[i].name)}>
              {data.map((d, i) => <Cell key={i} fill={TEC_COLORS[i % TEC_COLORS.length]} opacity={activeTecnicoName && activeTecnicoName !== d.name ? 0.35 : 1} />)}
              <LabelList dataKey="fatturato" position="right" formatter={(v) => `${eurK(v)} €`} style={{ fontSize: 10, fill: "#475569" }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}

function MediaOreFatturateChart({ chart, onTecnico, activeTecnicoName }) {
  const data = chart || [];
  const h = Math.max(200, data.length * 40 + 30);
  return (
    <Card className="rounded-xl border-slate-100 shadow-[0_2px_8px_-2px_rgba(0,57,106,0.08)] p-4" data-testid="rend-media-ore-fatturate">
      <h3 className="font-heading text-sm font-semibold flex items-center gap-1.5 mb-1"><Clock size={16} className="text-primary" /> Media ore fatturate</h3>
      <p className="text-xs text-muted-foreground mb-3">Ore lavorate medie per giorno di attività (incluse Garanzia/Comm./Costo copia)</p>
      {data.length === 0 ? <p className="text-xs text-muted-foreground py-10 text-center">Nessun dato nel periodo</p> : (
        <ResponsiveContainer width="100%" height={h}>
          <BarChart data={data} layout="vertical" margin={{ left: 10, right: 44 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID} horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}h`} />
            <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 10 }} />
            <Tooltip content={<MediaOreTooltip />} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
            <Bar dataKey="value" radius={[0, 3, 3, 0]} cursor="pointer" onClick={(_, i) => onTecnico && onTecnico(data[i].name)}>
              {data.map((d, i) => <Cell key={i} fill="#0b8457" opacity={activeTecnicoName && activeTecnicoName !== d.name ? 0.35 : 1} />)}
              <LabelList dataKey="value" position="right" formatter={(v) => `${fmtOre(v)} h`} style={{ fontSize: 10, fill: "#475569" }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}

function VariazioneTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-md bg-[#09090B] text-white text-xs p-2.5 space-y-0.5 min-w-[180px]">
      <div className="font-semibold mb-1">{p.name}</div>
      <div className="flex justify-between gap-4"><span className="text-white/60">Fatturato corrente</span><span>{euro(p.corrente || 0)}</span></div>
      <div className="flex justify-between gap-4"><span className="text-white/60">Fatturato precedente</span><span>{euro(p.precedente || 0)}</span></div>
      <div className="flex justify-between gap-4"><span className="text-white/60">Δ fatturato</span><span>{p.delta > 0 ? "+" : ""}{euro(p.delta || 0)}</span></div>
      <div className="flex justify-between gap-4"><span className="text-white/60">Δ %</span><span>{p.delta_pct == null ? "Nuovo" : `${p.delta_pct > 0 ? "+" : ""}${p.delta_pct}%`}</span></div>
    </div>
  );
}

function VariazioneFatturatoChart({ chart, onTecnico, activeTecnicoName }) {
  const data = (chart || []).filter((d) => (d.delta || 0) !== 0);
  const h = Math.max(180, data.length * 38 + 30);
  return (
    <Card className="rounded-xl border-slate-100 shadow-[0_2px_8px_-2px_rgba(0,57,106,0.08)] p-4" data-testid="rend-variazione-fatturato">
      <h3 className="font-heading text-sm font-semibold flex items-center gap-1.5 mb-1"><TrendUp size={16} className="text-primary" /> Variazione fatturato per tecnico</h3>
      <p className="text-xs text-muted-foreground mb-3">Delta vs periodo precedente equivalente</p>
      {data.length === 0 ? <p className="text-xs text-muted-foreground py-10 text-center">Nessuna variazione nel periodo</p> : (
        <ResponsiveContainer width="100%" height={h}>
          <BarChart data={data} layout="vertical" margin={{ left: 10, right: 48 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID} horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={eurK} />
            <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 10 }} />
            <Tooltip content={<VariazioneTooltip />} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
            <Bar dataKey="delta" radius={[0, 3, 3, 0]} cursor="pointer" onClick={(_, i) => onTecnico && onTecnico(data[i].name)}>
              {data.map((d, i) => <Cell key={i} fill={d.delta >= 0 ? "#0b8457" : "#e11d48"} opacity={activeTecnicoName && activeTecnicoName !== d.name ? 0.35 : 1} />)}
              <LabelList dataKey="delta" position="right" formatter={(v) => `${v > 0 ? "+" : ""}${eurK(v)} €`} style={{ fontSize: 10, fill: "#475569" }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}

function RiepilogoTecniciTable({ tecnici }) {
  const [sort, setSort] = useState({ key: "fatturato", dir: "desc" });
  const rows = useMemo(() => {
    const r = [...(tecnici || [])];
    const { key, dir } = sort;
    r.sort((a, b) => {
      let va = a[key], vb = b[key];
      if (key === "tecnico") { va = (va || "").toLowerCase(); vb = (vb || "").toLowerCase(); }
      va = va ?? -1; vb = vb ?? -1;
      if (va < vb) return dir === "asc" ? -1 : 1;
      if (va > vb) return dir === "asc" ? 1 : -1;
      return 0;
    });
    return r;
  }, [tecnici, sort]);
  const th = (k, label, cls = "") => (
    <th className={`px-3 py-2 font-semibold cursor-pointer select-none hover:text-foreground ${cls}`} onClick={() => setSort((s) => ({ key: k, dir: s.key === k && s.dir === "asc" ? "desc" : "asc" }))} data-testid={`rend-sort-${k}`}>
      <span className="inline-flex items-center gap-1">{label}{sort.key === k && (sort.dir === "asc" ? <CaretUp size={11} /> : <CaretDown size={11} />)}</span>
    </th>
  );
  const DeltaCell = ({ v, pct, kind, prevLabel }) => {
    if (v == null) return <td className="px-2 py-2 text-right tabular-nums text-muted-foreground">—</td>;
    const tone = v > 0 ? "text-emerald-600" : v < 0 ? "text-rose-600" : "text-muted-foreground";
    const val = kind === "eur" ? `${v > 0 ? "+" : ""}${euro(v)}` : kind === "h" ? `${v > 0 ? "+" : ""}${fmtOre(v)}h` : `${v > 0 ? "+" : ""}${v}`;
    const pctStr = pct === undefined ? "" : pct == null ? " · Nuovo" : ` · ${pct > 0 ? "+" : ""}${pct}%`;
    return <td className={`px-2 py-2 text-right tabular-nums text-[11px] ${tone}`} title={prevLabel}>{val}{pctStr}</td>;
  };
  return (
    <Card className="rounded-xl border-slate-100 shadow-[0_2px_8px_-2px_rgba(0,57,106,0.08)] overflow-hidden" data-testid="rend-riepilogo-tecnici">
      <h3 className="font-heading text-sm font-semibold flex items-center gap-1.5 p-4 pb-3"><IdentificationCard size={16} className="text-primary" /> Riepilogo tecnici <span className="text-[10px] font-normal text-muted-foreground">(Δ = vs periodo precedente)</span></h3>
      <div className="overflow-x-auto">
        <table className="w-full text-xs whitespace-nowrap">
          <thead className="bg-secondary/40 text-muted-foreground border-y border-border">
            <tr>
              {th("tecnico", "Tecnico", "text-left")}
              {th("fatturato", "Fatturato", "text-right")}
              <th className="px-2 py-2 font-semibold text-right">Δ fatt.</th>
              {th("ore_lavorate_totali", "Ore lavorate", "text-right")}
              <th className="px-2 py-2 font-semibold text-right">Δ ore</th>
              {th("ore_rendicontate", "Ore rend.", "text-right")}
              {th("ore_non_valorizzate", "Ore non val.", "text-right")}
              {th("media_euro_ora_reale", "€/h reale", "text-right")}
              <th className="px-2 py-2 font-semibold text-right">Δ €/h</th>
              {th("media_euro_ora_rendicontata", "€/h rendic.", "text-right")}
              {th("media_ore_fatturate", "Media ore fatt.", "text-right")}
              <th className="px-2 py-2 font-semibold text-right">Δ m.ore</th>
              <th className="px-3 py-2 font-semibold text-right">Media giorn.</th>
              {th("giorni_con_attivita", "Giorni", "text-right")}
              <th className="px-2 py-2 font-semibold text-right">Δ gg</th>
              <th className="px-3 py-2 font-semibold text-right">Rapp.</th>
              <th className="px-3 py-2 font-semibold text-right">% fatt.</th>
              <th className="px-3 py-2 font-semibold text-right">% ore lav.</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={18} className="px-4 py-8 text-center text-muted-foreground">Nessun dato nel periodo selezionato.</td></tr>}
            {rows.map((t) => {
              const dl = t.delta || {}; const pr = t.precedente || {};
              return (
              <tr key={t.tecnico} className="border-b border-border/60 hover:bg-secondary/40" data-testid={`rend-row-${t.tecnico}`}>
                <td className="px-3 py-2 font-medium">{t.tecnico}</td>
                <td className="px-3 py-2 text-right tabular-nums font-medium">{euro(t.fatturato)}</td>
                <DeltaCell v={dl.fatturato} pct={dl.fatturato_percentuale} kind="eur" prevLabel={`Precedente: ${euro(pr.fatturato || 0)}`} />
                <td className="px-3 py-2 text-right tabular-nums">{fmtOre(t.ore_lavorate_totali)}</td>
                <DeltaCell v={dl.ore_lavorate} pct={dl.ore_lavorate_percentuale} kind="h" prevLabel={`Precedente: ${fmtOre(pr.ore_lavorate_totali || 0)} h`} />
                <td className="px-3 py-2 text-right tabular-nums">{fmtOre(t.ore_rendicontate)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{fmtOre(t.ore_non_valorizzate)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{t.media_euro_ora_reale != null ? euro(t.media_euro_ora_reale) : "N/D"}</td>
                <DeltaCell v={dl.media_euro_ora_reale} kind="eur" prevLabel={`Precedente: ${pr.media_euro_ora_reale != null ? euro(pr.media_euro_ora_reale) : "N/D"}`} />
                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{t.media_euro_ora_rendicontata != null ? euro(t.media_euro_ora_rendicontata) : "N/D"}</td>
                <td className="px-3 py-2 text-right tabular-nums">{t.media_ore_fatturate != null ? `${fmtOre(t.media_ore_fatturate)} h` : "N/D"}</td>
                <DeltaCell v={dl.media_ore_fatturate} kind="h" prevLabel={`Precedente: ${pr.media_ore_fatturate != null ? fmtOre(pr.media_ore_fatturate) + " h" : "N/D"}`} />
                <td className="px-3 py-2 text-right tabular-nums">{t.media_giornaliera != null ? euro(t.media_giornaliera) : "N/D"}</td>
                <td className="px-3 py-2 text-right tabular-nums">{t.giorni_con_attivita}</td>
                <DeltaCell v={dl.giorni} kind="n" prevLabel={`Precedente: ${pr.giorni_con_attivita || 0} giorni`} />
                <td className="px-3 py-2 text-right tabular-nums">{t.rapportini}</td>
                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{t.pct_fatturato}%</td>
                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{t.pct_ore_lavorate}%</td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}


// Pannello filtri: variante "bar" (griglia sopra, scheda Ore Tecnici) o "sidebar" (colonna verticale, Report Ore)
function ReportFilters({ variant, filters, setF, refs, onSearch, resetFilters, tab, rowsLen, exportExcel, exportReportCompleto, exportPdf, exporting }) {
  const L = ({ children }) => <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">{children}</Label>;
  const fields = (
    <>
      <div><L>Data da</L><Input type="date" value={filters.date_from} onChange={(e) => setF("date_from", e.target.value)} className="mt-1 rounded-sm h-9" data-testid="filter-date-from" /></div>
      <div><L>Data a</L><Input type="date" value={filters.date_to} onChange={(e) => setF("date_to", e.target.value)} className="mt-1 rounded-sm h-9" data-testid="filter-date-to" /></div>
      <div><L>Tecnico</L><div className="mt-1"><MultiSelect width="w-full" testid="filter-tecnico" placeholder="Tutti" value={filters.tecnico_id} onChange={(v) => setF("tecnico_id", v)} searchable options={(refs.tecnici || []).map((t) => ({ value: t.id, label: t.name }))} /></div></div>
      <div><L>Cliente</L><div className="mt-1"><MultiSelect width="w-full" testid="filter-cliente" placeholder="Tutti" value={filters.cliente_id} onChange={(v) => setF("cliente_id", v)} searchable options={(refs.clients || []).map((c) => ({ value: c.id, label: c.ragione_sociale }))} /></div></div>
      <div><L>Stato</L><div className="mt-1"><MultiSelect width="w-full" testid="filter-stato" placeholder="Tutti" value={filters.stato} onChange={(v) => setF("stato", v)} options={(refs.stati || []).map((s) => ({ value: s.key, label: s.label }))} /></div></div>
      <div><L>Modalità</L><div className="mt-1"><MultiSelect width="w-full" testid="filter-modalita" placeholder="Tutte" value={filters.modalita_addebito} onChange={(v) => setF("modalita_addebito", v)} options={(refs.modalita || []).map((m) => ({ value: m.key, label: m.label }))} /></div></div>
      <div><L>Contratto</L><div className="mt-1"><MultiSelect width="w-full" testid="filter-contratto" placeholder="Tutti" value={filters.contratto_id} onChange={(v) => setF("contratto_id", v)} searchable options={(refs.contracts || []).map((c) => ({ value: c.id, label: c.nome }))} /></div></div>
      <div><L>Invio rapportino</L><div className="mt-1"><MultiSelect width="w-full" testid="filter-inviata" placeholder="Tutti" value={filters.inviata} onChange={(v) => setF("inviata", v)} options={[{ value: "true", label: "Inviata" }, { value: "false", label: "Non inviata" }]} /></div></div>
    </>
  );
  const searchField = (
    <div className={variant === "bar" ? "md:col-span-2 xl:col-span-3" : ""}>
      <L>Ricerca libera</L>
      <div className="relative mt-1"><MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
        <Input value={filters.search} onChange={(e) => onSearch(e.target.value)} placeholder="Numero, cliente, richiesta…" className="rounded-sm h-9 pl-8" data-testid="filter-search" /></div>
    </div>
  );
  if (variant === "sidebar") {
    return (
      <div className="space-y-3" data-testid="report-filters">
        <div className="flex items-center gap-1.5 pb-1"><Funnel size={16} className="text-primary" weight="fill" /><h3 className="font-heading text-sm font-semibold">Filtri</h3></div>
        {fields}
        {searchField}
        <div className="pt-1 space-y-2">
          <Button className="w-full rounded-sm h-9 gap-1.5" onClick={exportReportCompleto} disabled={exporting} data-testid={tab === "rendimento-tecnici" ? "export-rendimento" : tab === "clienti" ? "export-clienti" : "export-completo"}><DownloadSimple size={15} /> {exporting ? "Generazione…" : (tab === "rendimento-tecnici" ? "Esporta rendimento tecnici" : tab === "clienti" ? "Esporta clienti" : "Esporta report completo")}</Button>
          <Button variant="outline" className="w-full rounded-sm h-9 gap-1.5" onClick={exportPdf} disabled={exporting} data-testid="export-pdf"><DownloadSimple size={15} /> Esporta PDF</Button>
          <Button variant="outline" className="w-full rounded-sm h-9" onClick={resetFilters} data-testid="reset-filters">Reset filtri</Button>
        </div>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="report-filters">
      {fields}
      {searchField}
      <div className="flex items-end gap-2 flex-wrap">
        <Button variant="outline" className="rounded-sm h-9" onClick={resetFilters} data-testid="reset-filters">Reset</Button>
        <Button variant="outline" className="rounded-sm h-9 gap-1.5" onClick={exportPdf} disabled={exporting} data-testid="export-pdf"><DownloadSimple size={15} /> Esporta PDF</Button>
        {tab === "ore-tecnici" && <Button variant="outline" className="rounded-sm h-9 gap-1.5" onClick={exportExcel} disabled={!rowsLen} data-testid="export-excel"><DownloadSimple size={15} /> Esporta tabella</Button>}
      </div>
    </div>
  );
}

// Card KPI compatta con mini-delta (confronto periodo)
function KpiStat({ label, value, icon: Icon, accent, delta, deltaText, deltaTone, sub, tip }) {
  const up = delta != null && delta > 0, down = delta != null && delta < 0;
  const dColor = delta == null ? "" : up ? "text-emerald-600" : down ? "text-rose-600" : "text-muted-foreground";
  const DIcon = up ? ArrowUp : down ? ArrowDown : Minus;
  const toneColor = { pos: "text-emerald-600", neg: "text-rose-600", neu: "text-muted-foreground", amber: "text-amber-600", new: "text-primary" }[deltaTone] || "text-muted-foreground";
  return (
    <Card className="rounded-xl border-slate-100 shadow-[0_2px_8px_-2px_rgba(0,57,106,0.08)] p-3.5 transition-all duration-200 hover:shadow-md" title={tip} data-testid={`kpi-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</span>
        {Icon && <Icon size={15} className="text-slate-300" weight="fill" />}
      </div>
      <div className={`font-heading text-2xl font-bold mt-1 tabular-nums tracking-tight ${accent || "text-slate-800"}`}>{value}</div>
      <div className="flex items-center gap-2 mt-0.5 min-h-[16px] flex-wrap">
        {sub && <span className="text-[10px] text-muted-foreground">{sub}</span>}
        {deltaText != null ? (
          <span className={`text-[10px] font-semibold ${toneColor}`} data-testid={`kpi-cmp-${label.replace(/\s+/g, "-").toLowerCase()}`}>{deltaText}</span>
        ) : delta != null && (
          <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ${dColor}`} data-testid={`kpi-delta-${label.replace(/\s+/g, "-").toLowerCase()}`}>
            <DIcon size={10} weight="bold" />{delta > 0 ? "+" : ""}{delta.toLocaleString("it-IT", { maximumFractionDigits: 1 })}%
          </span>
        )}
      </div>
    </Card>
  );
}

// Gauge semicircolare (recharts PieChart 180→0)
function GaugeCard({ label, value, max, display, color, sub }) {
  const pct = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0;
  const dataG = [{ v: pct }, { v: 1 - pct }];
  return (
    <Card className="rounded-xl border-slate-100 shadow-[0_2px_8px_-2px_rgba(0,57,106,0.08)] p-3.5 flex flex-col" data-testid={`gauge-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
      <div className="relative flex-1 flex items-end justify-center" style={{ minHeight: 92 }}>
        <ResponsiveContainer width="100%" height={110}>
          <PieChart>
            <Pie data={dataG} dataKey="v" startAngle={180} endAngle={0} cx="50%" cy="90%" innerRadius="72%" outerRadius="100%" stroke="none" isAnimationActive={false}>
              <Cell fill={color} /><Cell fill="#eef2f6" />
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-x-0 bottom-1 text-center">
          <div className="font-heading text-xl font-bold tabular-nums text-slate-800">{display}</div>
          {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
        </div>
      </div>
    </Card>
  );
}

// Card Target (periodo/mese o anno) con barra di avanzamento e margine
function TargetGauge({ block, onConfigure, defaultLabel }) {
  if (!block || block.configurato === false || block.target == null) {
    return (
      <Card className="rounded-xl border-dashed border-slate-200 bg-slate-50/60 p-3.5 flex flex-col" data-testid={`target-${(defaultLabel || "target").replace(/\s+/g, "-").toLowerCase()}`}>
        <div className="flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{block?.anno ? `Target anno ${block.anno}` : defaultLabel}</span>
          <Target size={15} className="text-slate-300" weight="fill" />
        </div>
        <div className="text-sm text-slate-400 mt-2 mb-2">Target non configurato</div>
        <Button variant="outline" size="sm" className="rounded-sm h-8 text-xs mt-auto" onClick={onConfigure} data-testid="target-configura-btn">Configura target</Button>
      </Card>
    );
  }
  const pct = block.percentuale || 0;
  const barColor = pct >= 100 ? "#0b8457" : pct >= 90 ? "#d97706" : "#e11d48";
  const marginePos = (block.margine || 0) >= 0;
  const label = block.label || (block.anno ? `Target anno ${block.anno}` : defaultLabel);
  return (
    <Card className="rounded-xl border-slate-100 shadow-[0_2px_8px_-2px_rgba(0,57,106,0.08)] p-3.5 flex flex-col" data-testid={`target-gauge-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</span>
        <Target size={15} className="text-slate-300" weight="fill" />
      </div>
      <div className="font-heading text-lg font-bold tabular-nums text-slate-800 mt-1 leading-tight">{euro(block.valore || 0)}</div>
      <div className="text-[11px] text-muted-foreground tabular-nums">/ {euro(block.target || 0)}</div>
      <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(100, pct)}%`, backgroundColor: barColor }} />
      </div>
      <div className="flex items-center justify-between mt-1.5">
        <span className="text-[11px] font-semibold tabular-nums" style={{ color: barColor }}>{pct.toLocaleString("it-IT", { maximumFractionDigits: 1 })}%</span>
        <span className={`text-[10px] font-medium tabular-nums ${marginePos ? "text-emerald-600" : "text-rose-600"}`}>{marginePos ? "+" : ""}{euro(block.margine || 0)}</span>
      </div>
    </Card>
  );
}

// Card Proiezione fine anno (basata sulla ripartizione mensile del target, non lineare)
function ProiezioneCard({ block, onConfigure }) {
  if (!block || block.configurato === false || block.proiezione_fine_anno == null) {
    return (
      <Card className="rounded-xl border-dashed border-slate-200 bg-slate-50/60 p-3.5 flex flex-col" data-testid="proiezione-card">
        <div className="flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Proiezione fine anno</span>
          <TrendUp size={15} className="text-slate-300" weight="fill" />
        </div>
        <div className="text-sm text-slate-400 mt-2 mb-2">Target non configurato</div>
        <Button variant="outline" size="sm" className="rounded-sm h-8 text-xs mt-auto" onClick={onConfigure} data-testid="proiezione-configura-btn">Configura target</Button>
      </Card>
    );
  }
  const proiez = block.proiezione_fine_anno || 0, tgt = block.target_annuo || 0;
  const ratio = tgt > 0 ? (proiez / tgt) * 100 : 0;
  const color = ratio >= 100 ? "#0b8457" : ratio >= 90 ? "#d97706" : "#e11d48";
  const scost = block.scostamento_previsto || 0, scostPos = scost >= 0;
  const Row = ({ k, v, cls }) => (
    <div className="flex items-center justify-between py-0.5">
      <span className="text-[10px] text-muted-foreground">{k}</span>
      <span className={`text-[11px] font-medium tabular-nums ${cls || "text-slate-700"}`}>{v}</span>
    </div>
  );
  return (
    <Card className="rounded-xl border-slate-100 shadow-[0_2px_8px_-2px_rgba(0,57,106,0.08)] p-3.5 flex flex-col" data-testid="proiezione-card">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Proiezione fine anno</span>
        <TrendUp size={15} weight="fill" style={{ color }} />
      </div>
      <div className="font-heading text-xl font-bold tabular-nums mt-1 leading-tight" style={{ color }}>{euro(proiez)}</div>
      <div className="mt-2 pt-2 border-t border-slate-100 space-y-0.5">
        <Row k="Target annuo" v={euro(tgt)} />
        <Row k="Scostamento previsto" v={`${scostPos ? "+" : ""}${euro(scost)}`} cls={scostPos ? "text-emerald-600" : "text-rose-600"} />
        <Row k="Su target maturato" v={`${(block.percentuale_su_target_maturato || 0).toLocaleString("it-IT", { maximumFractionDigits: 2 })}%`} />
        <Row k="Valore YTD" v={euro(block.valore_ytd || 0)} />
        <Row k="Target maturato" v={euro(block.target_maturato || 0)} />
      </div>
    </Card>
  );
}

// ===== CLIENTI =====
const CLI_COLORS = ["#00396a", "#0b8457", "#d97706", "#7c3aed", "#dc2626", "#0891b2", "#db2777", "#65a30d", "#ea580c", "#2563eb", "#059669", "#9333ea", "#0d9488", "#c026d3", "#b45309", "#475569"];

function ClienteFattTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-md bg-[#09090B] text-white text-xs p-2.5 space-y-0.5 min-w-[200px]">
      <div className="font-semibold mb-1">{p.name}</div>
      <div className="flex justify-between gap-4"><span className="text-white/60">Fatturato prodotto</span><span>{euro(p.fatturato || 0)}</span></div>
      <div className="flex justify-between gap-4"><span className="text-white/60">Ore lavorate totali</span><span>{fmtOre(p.ore_lavorate_totali || 0)} h</span></div>
      <div className="flex justify-between gap-4"><span className="text-white/60">Ore rendicontate</span><span>{fmtOre(p.ore_rendicontate || 0)} h</span></div>
      <div className="flex justify-between gap-4"><span className="text-white/60">Ore non valorizzate</span><span>{fmtOre(p.ore_non_valorizzate || 0)} h</span></div>
      <div className="flex justify-between gap-4"><span className="text-white/60">Media €/h reale</span><span>{p.media_euro_ora_reale != null ? euro(p.media_euro_ora_reale) : "N/D"}</span></div>
      <div className="flex justify-between gap-4"><span className="text-white/60">Rapportini</span><span>{p.rapportini || 0}</span></div>
    </div>
  );
}

function FatturatoPerClienteChart({ chart, onCliente, activeClienteName }) {
  const data = chart || [];
  const h = Math.max(200, data.length * 40 + 30);
  return (
    <Card className="rounded-xl border-slate-100 shadow-[0_2px_8px_-2px_rgba(0,57,106,0.08)] p-4" data-testid="cli-fatturato-per-cliente">
      <h3 className="font-heading text-sm font-semibold flex items-center gap-1.5 mb-1"><CurrencyEur size={16} className="text-primary" /> Fatturato per cliente</h3>
      <p className="text-xs text-muted-foreground mb-3">Clicca un cliente per filtrare la dashboard</p>
      {data.length === 0 ? <p className="text-xs text-muted-foreground py-10 text-center" data-testid="cli-fatturato-empty">Nessun dato nel periodo</p> : (
        <ResponsiveContainer width="100%" height={h}>
          <BarChart data={data} layout="vertical" margin={{ left: 10, right: 44 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID} horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={eurK} />
            <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 10 }} />
            <Tooltip content={<ClienteFattTooltip />} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
            <Bar dataKey="fatturato" radius={[0, 3, 3, 0]} cursor="pointer" onClick={(_, i) => onCliente && onCliente(data[i].name)}>
              {data.map((d, i) => <Cell key={i} fill={CLI_COLORS[i % CLI_COLORS.length]} opacity={activeClienteName && activeClienteName !== d.name ? 0.35 : 1} />)}
              <LabelList dataKey="fatturato" position="right" formatter={(v) => `${eurK(v)} €`} style={{ fontSize: 10, fill: "#475569" }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}

function OreClienteTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-md bg-[#09090B] text-white text-xs p-2.5 space-y-0.5 min-w-[200px]">
      <div className="font-semibold mb-1">{p.name}</div>
      <div className="flex justify-between gap-4"><span className="text-white/60">Ore lavorate totali</span><span>{fmtOre(p.ore_lavorate_totali || 0)} h</span></div>
      <div className="flex justify-between gap-4"><span className="text-emerald-400">Rendicontate</span><span>{fmtOre(p.ore_rendicontate || 0)} h</span></div>
      <div className="flex justify-between gap-4"><span className="text-white/40">Non valorizzate</span><span>{fmtOre(p.ore_non_valorizzate || 0)} h</span></div>
      <div className="flex justify-between gap-4 mt-1 pt-1 border-t border-white/15"><span className="text-white/60">Fatturato</span><span>{euro(p.fatturato || 0)}</span></div>
    </div>
  );
}

function OrePerClienteChart({ chart, onCliente, activeClienteName }) {
  const data = chart || [];
  const h = Math.max(200, data.length * 40 + 30);
  return (
    <Card className="rounded-xl border-slate-100 shadow-[0_2px_8px_-2px_rgba(0,57,106,0.08)] p-4" data-testid="cli-ore-per-cliente">
      <h3 className="font-heading text-sm font-semibold flex items-center gap-1.5 mb-1"><Clock size={16} className="text-primary" /> Ore lavorate per cliente</h3>
      <p className="text-xs text-muted-foreground mb-3">Rendicontate + non valorizzate (Garanzia/Comm./Costo copia)</p>
      {data.length === 0 ? <p className="text-xs text-muted-foreground py-10 text-center">Nessun dato nel periodo</p> : (
        <ResponsiveContainer width="100%" height={h}>
          <BarChart data={data} layout="vertical" margin={{ left: 10, right: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID} horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}h`} />
            <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 10 }} />
            <Tooltip content={<OreClienteTooltip />} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
            <Legend verticalAlign="top" align="left" wrapperStyle={{ fontSize: 11, paddingBottom: 8 }} />
            <Bar dataKey="ore_rendicontate" name="Rendicontate" stackId="o" fill="#0b8457" cursor="pointer" onClick={(_, i) => onCliente && onCliente(data[i].name)}>
              {data.map((d, i) => <Cell key={i} opacity={activeClienteName && activeClienteName !== d.name ? 0.35 : 1} />)}
            </Bar>
            <Bar dataKey="ore_non_valorizzate" name="Non valorizzate" stackId="o" fill="#cbd5e1" radius={[0, 3, 3, 0]} cursor="pointer" onClick={(_, i) => onCliente && onCliente(data[i].name)}>
              {data.map((d, i) => <Cell key={i} opacity={activeClienteName && activeClienteName !== d.name ? 0.35 : 1} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}

function MediaOraClienteTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-md bg-[#09090B] text-white text-xs p-2.5 space-y-0.5 min-w-[200px]">
      <div className="font-semibold mb-1">{p.name}</div>
      <div className="flex justify-between gap-4"><span className="text-white/60">Media €/h reale</span><span>{euro(p.value || 0)}</span></div>
      <div className="flex justify-between gap-4"><span className="text-white/60">Fatturato prodotto</span><span>{euro(p.fatturato || 0)}</span></div>
      <div className="flex justify-between gap-4"><span className="text-white/60">Ore lavorate totali</span><span>{fmtOre(p.ore_lavorate_totali || 0)} h</span></div>
      <div className="flex justify-between gap-4"><span className="text-white/60">Ore rendicontate</span><span>{fmtOre(p.ore_rendicontate || 0)} h</span></div>
      <div className="flex justify-between gap-4"><span className="text-white/60">Ore non valorizzate</span><span>{fmtOre(p.ore_non_valorizzate || 0)} h</span></div>
    </div>
  );
}

function MediaOraClienteChart({ chart, onCliente, activeClienteName }) {
  const data = chart || [];
  const h = Math.max(200, data.length * 40 + 30);
  return (
    <Card className="rounded-xl border-slate-100 shadow-[0_2px_8px_-2px_rgba(0,57,106,0.08)] p-4" data-testid="cli-media-oraria">
      <h3 className="font-heading text-sm font-semibold flex items-center gap-1.5 mb-1"><TrendUp size={16} className="text-primary" /> Media €/h per cliente</h3>
      <p className="text-xs text-muted-foreground mb-3">Fatturato prodotto / ore lavorate totali</p>
      {data.length === 0 ? <p className="text-xs text-muted-foreground py-10 text-center">Nessun dato nel periodo</p> : (
        <ResponsiveContainer width="100%" height={h}>
          <BarChart data={data} layout="vertical" margin={{ left: 10, right: 44 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID} horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={eurK} />
            <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 10 }} />
            <Tooltip content={<MediaOraClienteTooltip />} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
            <Bar dataKey="value" fill="#7c3aed" radius={[0, 3, 3, 0]} cursor="pointer" onClick={(_, i) => onCliente && onCliente(data[i].name)}>
              {data.map((d, i) => <Cell key={i} opacity={activeClienteName && activeClienteName !== d.name ? 0.35 : 1} />)}
              <LabelList dataKey="value" position="right" formatter={(v) => `${euro(v)}`} style={{ fontSize: 10, fill: "#475569" }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}

function RiepilogoClientiTable({ clienti, onCliente, onOpen }) {
  const [sort, setSort] = useState({ key: "fatturato", dir: "desc" });
  const rows = useMemo(() => {
    const r = [...(clienti || [])];
    const { key, dir } = sort;
    r.sort((a, b) => {
      let va = a[key], vb = b[key];
      if (key === "cliente") { va = (va || "").toLowerCase(); vb = (vb || "").toLowerCase(); }
      va = va ?? -1; vb = vb ?? -1;
      if (va < vb) return dir === "asc" ? -1 : 1;
      if (va > vb) return dir === "asc" ? 1 : -1;
      return 0;
    });
    return r;
  }, [clienti, sort]);
  const th = (k, label, cls = "") => (
    <th className={`px-3 py-2 font-semibold cursor-pointer select-none hover:text-foreground ${cls}`} onClick={() => setSort((s) => ({ key: k, dir: s.key === k && s.dir === "asc" ? "desc" : "asc" }))} data-testid={`cli-sort-${k}`}>
      <span className="inline-flex items-center gap-1">{label}{sort.key === k && (sort.dir === "asc" ? <CaretUp size={11} /> : <CaretDown size={11} />)}</span>
    </th>
  );
  return (
    <Card className="rounded-xl border-slate-100 shadow-[0_2px_8px_-2px_rgba(0,57,106,0.08)] overflow-hidden" data-testid="cli-riepilogo-clienti">
      <h3 className="font-heading text-sm font-semibold flex items-center gap-1.5 p-4 pb-3"><IdentificationCard size={16} className="text-primary" /> Riepilogo clienti</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-secondary/50 text-muted-foreground border-y border-border">
            <tr>
              {th("cliente", "Cliente", "text-left")}
              {th("fatturato", "Fatturato", "text-right")}
              {th("ore_lavorate_totali", "Ore lav.", "text-right")}
              <th className="px-3 py-2 font-semibold text-right">Ore rend.</th>
              <th className="px-3 py-2 font-semibold text-right">Ore n.v.</th>
              {th("media_euro_ora_reale", "€/h reale", "text-right")}
              {th("rapportini", "Rapp.", "text-right")}
              <th className="px-3 py-2 font-semibold text-right">Tecnici</th>
              <th className="px-3 py-2 font-semibold text-left">Modalità prev.</th>
              <th className="px-3 py-2 font-semibold text-left">Contratti</th>
              {th("righe_da_verificare", "Da verif.", "text-right")}
              <th className="px-3 py-2 font-semibold text-right">% fatt.</th>
              <th className="px-3 py-2 font-semibold text-right">% ore</th>
              <th className="px-3 py-2 font-semibold text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={14} className="px-3 py-8 text-center text-muted-foreground" data-testid="cli-table-empty">Nessun cliente nel periodo selezionato</td></tr>
            ) : rows.map((c, i) => (
              <tr key={i} className="border-b border-border/60 hover:bg-secondary/30 cursor-pointer" onClick={() => onCliente && onCliente(c.cliente)} data-testid={`cli-row-${i}`}>
                <td className="px-3 py-2 font-medium">{c.cliente}</td>
                <td className="px-3 py-2 text-right tabular-nums">{euro(c.fatturato || 0)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtOre(c.ore_lavorate_totali || 0)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-emerald-600">{fmtOre(c.ore_rendicontate || 0)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{fmtOre(c.ore_non_valorizzate || 0)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{c.media_euro_ora_reale != null ? euro(c.media_euro_ora_reale) : "N/D"}</td>
                <td className="px-3 py-2 text-right tabular-nums">{c.rapportini || 0}</td>
                <td className="px-3 py-2 text-right tabular-nums">{c.tecnici_coinvolti || 0}</td>
                <td className="px-3 py-2">{c.modalita_prevalente || "—"}</td>
                <td className="px-3 py-2 max-w-[180px] truncate" title={(c.contratti_collegati || []).join(", ")}>{(c.contratti_collegati || []).join(", ") || "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums">{c.righe_da_verificare > 0 ? <span className="text-amber-600 font-semibold">{c.righe_da_verificare}</span> : "0"}</td>
                <td className="px-3 py-2 text-right tabular-nums">{c.pct_fatturato}%</td>
                <td className="px-3 py-2 text-right tabular-nums">{c.pct_ore_lavorate}%</td>
                <td className="px-3 py-2 text-right">
                  {c.cliente_id && <button className="text-primary hover:underline" onClick={(e) => { e.stopPropagation(); onOpen && onOpen(c.cliente_id); }} data-testid={`cli-open-${i}`}>Apri</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}


function OreTecnici({ tab }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [filters, setFilters] = useState(() => ({
    ...monthDefaults(), ...emptyMulti(), search: "",
  }));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refs, setRefs] = useState({ tecnici: [], clients: [], stati: [], modalita: [], contracts: [] });
  const [sort, setSort] = useState({ key: "data", dir: "desc" });
  const [granSel, setGranSel] = useState("");
  const [exporting, setExporting] = useState(false);
  const [soloDaVerificare, setSoloDaVerificare] = useState(false);
  const [rendData, setRendData] = useState(null);
  const [rendLoading, setRendLoading] = useState(false);
  const [clientiData, setClientiData] = useState(null);
  const [clientiLoading, setClientiLoading] = useState(false);
  const [sogliaFatt, setSogliaFatt] = useState(0);
  const [cliSoloVerif, setCliSoloVerif] = useState(false);
  const [ovrRow, setOvrRow] = useState(null);
  const [ovrImporto, setOvrImporto] = useState("");
  const [ovrMotivo, setOvrMotivo] = useState("");
  const [ovrSaving, setOvrSaving] = useState(false);
  const searchTimer = useRef(null);
  const prefsHydrated = useRef(false);
  const prefsKey = user?.id ? REPORT_PREFS_PREFIX + user.id : null;
  const [sidebarW, setSidebarW] = useState(() => {
    const v = parseInt(localStorage.getItem("reportistica_sidebar_w"), 10);
    return v && v >= 180 && v <= 420 ? v : 230;
  });
  const startResize = (e) => {
    e.preventDefault();
    const startX = e.clientX, startW = sidebarW;
    const onMove = (ev) => {
      const w = Math.max(180, Math.min(420, startW + (ev.clientX - startX)));
      setSidebarW(w);
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = "";
    };
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };
  useEffect(() => { try { localStorage.setItem("reportistica_sidebar_w", String(sidebarW)); } catch { /* noop */ } }, [sidebarW]);
  const [isDesktop, setIsDesktop] = useState(typeof window !== "undefined" ? window.innerWidth >= 1024 : true);
  useEffect(() => {
    const on = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  const toggleTecnico = (name) => {
    const t = (refs.tecnici || []).find((x) => x.name === name);
    if (!t?.id) return;
    setFilters((s) => {
      const cur = Array.isArray(s.tecnico_id) ? s.tecnico_id : [];
      return { ...s, tecnico_id: cur.length === 1 && cur[0] === t.id ? [] : [t.id] };
    });
  };
  const activeTecnicoName = (() => {
    const cur = Array.isArray(filters.tecnico_id) ? filters.tecnico_id : [];
    return cur.length === 1 ? ((refs.tecnici || []).find((x) => x.id === cur[0])?.name || null) : null;
  })();
  const toggleCliente = (name) => {
    const c = (refs.clients || []).find((x) => x.ragione_sociale === name);
    if (!c?.id) return;
    setFilters((s) => {
      const cur = Array.isArray(s.cliente_id) ? s.cliente_id : [];
      return { ...s, cliente_id: cur.length === 1 && cur[0] === c.id ? [] : [c.id] };
    });
  };
  const activeClienteName = (() => {
    const cur = Array.isArray(filters.cliente_id) ? filters.cliente_id : [];
    return cur.length === 1 ? ((refs.clients || []).find((x) => x.id === cur[0])?.ragione_sociale || null) : null;
  })();

  const openOverride = (e, r) => {
    e.stopPropagation();
    setOvrRow(r);
    setOvrImporto(String(r.override_attivo ? (r.importo_override ?? "") : (r.importo_calcolato ?? "")));
    setOvrMotivo(r.override_motivo || "");
  };
  const saveOverride = async () => {
    if (!ovrRow) return;
    const imp = parseFloat(String(ovrImporto).replace(",", "."));
    if (isNaN(imp) || imp < 0) { toast.error("Inserisci un importo valido (≥ 0)"); return; }
    setOvrSaving(true);
    try {
      await api.patch(`/reportistica/assistenza-tecnica/rapportini/${ovrRow.id}/override-importo`, { attivo: true, importo: imp, motivo: ovrMotivo });
      toast.success("Importo sovrascritto");
      setOvrRow(null);
      fetchData(filters, granSel);
    } catch { toast.error("Errore nel salvataggio dell'override"); }
    finally { setOvrSaving(false); }
  };
  const removeOverride = async () => {
    if (!ovrRow) return;
    setOvrSaving(true);
    try {
      await api.patch(`/reportistica/assistenza-tecnica/rapportini/${ovrRow.id}/override-importo`, { attivo: false });
      toast.success("Override rimosso, ripristinato importo calcolato");
      setOvrRow(null);
      fetchData(filters, granSel);
    } catch { toast.error("Errore nella rimozione dell'override"); }
    finally { setOvrSaving(false); }
  };

  useEffect(() => {
    Promise.all([
      api.get("/tecnici").catch(() => ({ data: [] })),
      api.get("/clients").catch(() => ({ data: [] })),
      api.get("/interventi-config").catch(() => ({ data: {} })),
      api.get("/contracts").catch(() => ({ data: [] })),
    ]).then(([tec, cli, cfg, ctr]) => setRefs({
      tecnici: tec.data || [], clients: cli.data || [],
      stati: (cfg.data?.stati || []), modalita: (cfg.data?.modalita || []),
      contracts: ctr.data || [],
    }));
  }, []);

  const fetchData = useCallback((f, g) => {
    setLoading(true);
    const params = serializeFilters(f);
    if (g) params.granularita = g;
    api.get("/reportistica/assistenza-tecnica/ore-tecnici", { params })
      .then(({ data }) => setData(data)).catch(() => setData({ kpi: {}, rows: [], charts: {} }))
      .finally(() => setLoading(false));
  }, []);

  const fetchRendimento = useCallback((f, g) => {
    setRendLoading(true);
    const params = serializeFilters(f);
    if (g) params.granularita = g;
    api.get("/reportistica/assistenza-tecnica/rendimento-tecnici", { params })
      .then(({ data }) => setRendData(data)).catch(() => setRendData({ kpi: {}, tecnici: [], charts: {} }))
      .finally(() => setRendLoading(false));
  }, []);

  const fetchClienti = useCallback((f, soglia, soloVerif) => {
    setClientiLoading(true);
    const params = serializeFilters(f);
    if (soglia) params.soglia_fatturato_minimo = soglia;
    if (soloVerif) params.solo_da_verificare = "true";
    api.get("/reportistica/assistenza-tecnica/clienti", { params })
      .then(({ data }) => setClientiData(data)).catch(() => setClientiData({ kpi: {}, clienti: [], charts: {} }))
      .finally(() => setClientiLoading(false));
  }, []);

  useEffect(() => { fetchData(filters, granSel); }, [
    filters.date_from, filters.date_to, filters.tecnico_id, filters.cliente_id,
    filters.stato, filters.modalita_addebito, filters.contratto_id, filters.inviata, granSel, fetchData, filters,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tab === "rendimento-tecnici") fetchRendimento(filters, granSel);
  }, [tab, filters.date_from, filters.date_to, filters.tecnico_id, filters.cliente_id,
    filters.stato, filters.modalita_addebito, filters.contratto_id, filters.inviata, granSel, fetchRendimento, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tab === "clienti") fetchClienti(filters, sogliaFatt, cliSoloVerif);
  }, [tab, filters.date_from, filters.date_to, filters.tecnico_id, filters.cliente_id,
    filters.stato, filters.modalita_addebito, filters.contratto_id, filters.inviata,
    sogliaFatt, cliSoloVerif, fetchClienti, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Idratazione preferenze filtri salvate per l'utente (una sola volta, quando l'utente è disponibile)
  useEffect(() => {
    if (!prefsKey || prefsHydrated.current) return;
    const p = loadReportPrefs(prefsKey);
    if (p.filters) setFilters((s) => normalizeMulti({ ...s, ...p.filters }));
    if (p.sort) setSort(p.sort);
    if (typeof p.granSel === "string") setGranSel(p.granSel);
    prefsHydrated.current = true;
  }, [prefsKey]);

  // Salvataggio preferenze filtri/ordinamento/granularità (solo dopo l'idratazione)
  useEffect(() => {
    if (!prefsKey || !prefsHydrated.current) return;
    try { localStorage.setItem(prefsKey, JSON.stringify({ filters, sort, granSel })); } catch { /* noop */ }
  }, [filters, sort, granSel, prefsKey]);

  const setF = (k, v) => setFilters((s) => ({ ...s, [k]: v === "__all" ? "" : v }));
  const onSearch = (v) => {
    setFilters((s) => ({ ...s, search: v }));
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => fetchData({ ...filters, search: v }, granSel), 400);
  };
  const resetFilters = () => setFilters({ ...monthDefaults(), ...emptyMulti(), search: "" });

  const rows = useMemo(() => {
    let r = [...(data?.rows || [])];
    if (soloDaVerificare) r = r.filter((x) => x.da_verificare);
    const { key, dir } = sort;
    r.sort((a, b) => {
      let va = a[key], vb = b[key];
      if (key === "cliente_nome" || key === "tecnico_nome") { va = (va || "").toLowerCase(); vb = (vb || "").toLowerCase(); }
      if (va < vb) return dir === "asc" ? -1 : 1;
      if (va > vb) return dir === "asc" ? 1 : -1;
      return 0;
    });
    return r;
  }, [data, sort, soloDaVerificare]);
  const nDaVerificare = data?.conteggio_da_verificare ?? (data?.rows || []).filter((r) => r.da_verificare).length;

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));
  const SortH = ({ k, children, className = "" }) => (
    <th className={`px-3 py-2 text-left font-semibold cursor-pointer select-none hover:text-foreground ${className}`} onClick={() => toggleSort(k)} data-testid={`sort-${k}`}>
      <span className="inline-flex items-center gap-1">{children}{sort.key === k && (sort.dir === "asc" ? <CaretUp size={11} /> : <CaretDown size={11} />)}</span>
    </th>
  );

  const totOre = rows.reduce((s, r) => s + (r.ore || 0), 0);
  const totImp = rows.reduce((s, r) => s + (r.importo_stimato || 0), 0);

  const exportExcel = () => {
    const head = ["Data", "Numero", "Cliente", "Tecnico", "Stato", "Modalità", "Contratto", "Ore", "Tariffa", "Importo stimato", "Metodo valorizzazione", "Da verificare", "Motivo da verificare", "Inviata", "Richiesta"];
    const lines = rows.map((r) => [
      fmtD(r.data), r.numero, r.cliente_nome, r.tecnico_nome, r.stato_label, r.modalita_label, r.contratto_nome,
      fmtOre(r.ore), r.tariffa == null ? "Da configurare" : fmtOre(r.tariffa), r.tariffa == null ? "" : fmtOre(r.importo_stimato),
      r.metodo_valorizzazione || "", r.da_verificare ? "Sì" : "No", r.motivo_da_verificare || "",
      r.inviata ? "Sì" : "No", (r.richiesta || "").replace(/[\r\n;]+/g, " "),
    ].map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";"));
    const csv = "\uFEFF" + [head.join(";"), ...lines].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `Ore_Tecnici_${filters.date_from}_${filters.date_to}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const exportReportCompleto = async () => {
    setExporting(true);
    try {
      const params = serializeFilters(filters);
      if (granSel) params.granularita = granSel;
      if (soloDaVerificare) params.solo_da_verificare = "true";
      const res = await api.get("/reportistica/assistenza-tecnica/ore-tecnici/export-completo", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Report_Ore_Tecnici_${filters.date_from || "inizio"}_${filters.date_to || "fine"}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Impossibile generare il report Excel. Riprova o contatta l'amministratore.");
    } finally {
      setExporting(false);
    }
  };

  const exportRendimento = async () => {
    setExporting(true);
    try {
      const params = serializeFilters(filters);
      if (granSel) params.granularita = granSel;
      const res = await api.get("/reportistica/assistenza-tecnica/rendimento-tecnici/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Rendimento_Tecnici_${filters.date_from || "inizio"}_${filters.date_to || "fine"}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Impossibile generare l'export rendimento. Riprova o contatta l'amministratore.");
    } finally {
      setExporting(false);
    }
  };

  const exportClienti = async () => {
    setExporting(true);
    try {
      const params = serializeFilters(filters);
      if (sogliaFatt) params.soglia_fatturato_minimo = sogliaFatt;
      if (cliSoloVerif) params.solo_da_verificare = "true";
      const res = await api.get("/reportistica/assistenza-tecnica/clienti/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Clienti_${filters.date_from || "inizio"}_${filters.date_to || "fine"}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Impossibile generare l'export clienti. Riprova o contatta l'amministratore.");
    } finally {
      setExporting(false);
    }
  };

  const kpi = data?.kpi || {};
  const charts = data?.charts || {};

  const exportPdf = async () => {
    setExporting(true);
    try {
      const params = serializeFilters(filters);
      if (granSel) params.granularita = granSel;
      if (tab === "clienti") { if (sogliaFatt) params.soglia_fatturato_minimo = sogliaFatt; if (cliSoloVerif) params.solo_da_verificare = "true"; }
      if (tab === "ore-tecnici" && soloDaVerificare) params.solo_da_verificare = "true";
      const paths = {
        "ore-tecnici": "/reportistica/assistenza-tecnica/ore-tecnici/pdf",
        "report-ore": "/reportistica/assistenza-tecnica/report-ore/pdf",
        "rendimento-tecnici": "/reportistica/assistenza-tecnica/rendimento-tecnici/pdf",
        "clienti": "/reportistica/assistenza-tecnica/clienti/pdf",
      };
      const res = await api.get(paths[tab] || paths["ore-tecnici"], { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Report_${tab}_${filters.date_from || "inizio"}_${filters.date_to || "fine"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Impossibile generare il PDF. Riprova o contatta l'amministratore.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Filtri — scheda Ore Tecnici (barra in alto) */}
      {tab === "ore-tecnici" && (
        <Card className="rounded-sm border-border shadow-none p-4">
          <ReportFilters variant="bar" filters={filters} setF={setF} refs={refs} onSearch={onSearch} resetFilters={resetFilters} tab={tab} rowsLen={rows.length} exportExcel={exportExcel} exportReportCompleto={exportReportCompleto} exporting={exporting} exportPdf={exportPdf} />
        </Card>
      )}

      {tab === "report-ore" && (
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-0">
          {/* Colonna filtri ridimensionabile (sticky su desktop) */}
          <aside className="w-full lg:flex-shrink-0" style={isDesktop ? { width: sidebarW } : undefined}>
            <Card className="rounded-xl border-slate-100 shadow-[0_2px_8px_-2px_rgba(0,57,106,0.08)] p-4 lg:sticky lg:top-16">
              <ReportFilters variant="sidebar" filters={filters} setF={setF} refs={refs} onSearch={onSearch} resetFilters={resetFilters} tab={tab} rowsLen={rows.length} exportExcel={exportExcel} exportReportCompleto={exportReportCompleto} exporting={exporting} exportPdf={exportPdf} />
            </Card>
          </aside>
          {/* Maniglia di ridimensionamento (solo desktop) */}
          <div className="hidden lg:flex items-stretch w-3 cursor-col-resize group flex-shrink-0" onMouseDown={startResize} data-testid="sidebar-resize-handle" title="Trascina per ridimensionare">
            <div className="w-0.5 mx-auto rounded-full bg-slate-200 group-hover:bg-primary transition-colors" />
          </div>

          {/* Area dashboard */}
          <div className="flex-1 min-w-0 space-y-5">
            {activeTecnicoName && (
              <div className="flex items-center gap-2 text-xs bg-primary/5 border border-primary/20 rounded-lg px-3 py-1.5 w-fit" data-testid="tecnico-filter-badge">
                <span className="text-primary font-medium">Filtro tecnico: {activeTecnicoName}</span>
                <button onClick={() => setFilters((s) => ({ ...s, tecnico_id: "" }))} className="text-primary/70 hover:text-primary" data-testid="tecnico-filter-clear">✕</button>
              </div>
            )}
            {loading && !data ? (
              <div className="space-y-5">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
                <Skeleton className="h-[320px] rounded-xl" />
                <Skeleton className="h-[320px] rounded-xl" />
              </div>
            ) : (() => {
              const dv = (k) => data?.confronto_periodo?.variazioni?.[k]?.delta_percentuale ?? null;
              const med = data?.medie || {};
              const rot = data?.report_ore_target || {};
              return (
                <>
                  {/* Strip KPI principali con delta vs periodo precedente */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <KpiStat label="Ore totali" value={fmtOre(kpi.totale_ore || 0)} icon={Clock} delta={dv("totale_ore")} />
                    <KpiStat label="Ore rendicontate" value={fmtOre(kpi.totale_ore_rendicontate || 0)} icon={Clock} accent="text-emerald-600" delta={dv("totale_ore_rendicontate")} tip="Produzione tecnica: tutte le modalità tranne Garanzia, Commerciale e Costo Copia." />
                    <KpiStat label="Ore escluse" value={fmtOre(kpi.totale_ore_escluse)} icon={Minus} sub="Garanzia · Comm. · Costo copia" tip="Escluse dalla produzione." />
                    <KpiStat label="Fatturato periodo selezionato" value={euro(kpi.totale_importo_stimato || 0)} icon={CurrencyEur} accent="text-primary" delta={dv("totale_importo_stimato")} tip="Fatturato del periodo secondo le logiche economiche validate (consuntivo, assistenza, FLAT, override)." />
                  </div>

                  {/* Grafico principale: andamento ore per tecnico (cross-filter) */}
                  <AndamentoOre data={data} activeGran={granSel} onGran={setGranSel} onTecnico={toggleTecnico} activeTecnicoName={activeTecnicoName} />

                  {/* Fatturato Mensile grande */}
                  <FatturatoMensile data={data} />

                  {/* Fascia card medie + target + proiezione */}
                  <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                    <MediaCard label="Media fatt. giornaliera" value={med.media_fatturato_giornaliera || 0} target={rot.target_media_fatturato_giornaliera ?? null} onConfigure={() => nav("/impostazioni")} />
                    <MediaCard label="Media fatt. oraria" value={med.media_fatturato_oraria || 0} target={rot.target_media_fatturato_oraria ?? null} onConfigure={() => nav("/impostazioni")} />
                    <TargetGauge block={data?.target?.target_periodo} defaultLabel="Target mese" onConfigure={() => nav("/impostazioni")} />
                    <TargetGauge block={data?.target?.target_anno} defaultLabel="Target anno" onConfigure={() => nav("/impostazioni")} />
                    <ProiezioneCard block={data?.target?.proiezione_annuale} onConfigure={() => nav("/impostazioni")} />
                  </div>

                  {/* Riepilogo finale: fatturato per modalità */}
                  <ChartCard title="Fatturato per modalità di addebito" data={charts.fatturato_per_modalita_addebito || []} color="#0b8457" money />
                </>
              );
            })()}
          </div>
        </div>
      )}

      {tab === "rendimento-tecnici" && (
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-0">
          <aside className="w-full lg:flex-shrink-0" style={isDesktop ? { width: sidebarW } : undefined}>
            <Card className="rounded-xl border-slate-100 shadow-[0_2px_8px_-2px_rgba(0,57,106,0.08)] p-4 lg:sticky lg:top-16">
              <ReportFilters variant="sidebar" filters={filters} setF={setF} refs={refs} onSearch={onSearch} resetFilters={resetFilters} tab={tab} rowsLen={(rendData?.tecnici || []).length} exportExcel={exportExcel} exportReportCompleto={exportRendimento} exporting={exporting} exportPdf={exportPdf} />
            </Card>
          </aside>
          <div className="hidden lg:flex items-stretch w-3 cursor-col-resize group flex-shrink-0" onMouseDown={startResize} title="Trascina per ridimensionare">
            <div className="w-0.5 mx-auto rounded-full bg-slate-200 group-hover:bg-primary transition-colors" />
          </div>
          <div className="flex-1 min-w-0 space-y-5">
            {activeTecnicoName && (
              <div className="flex items-center gap-2 text-xs bg-primary/5 border border-primary/20 rounded-lg px-3 py-1.5 w-fit" data-testid="rend-tecnico-filter-badge">
                <span className="text-primary font-medium">Filtro tecnico: {activeTecnicoName}</span>
                <button onClick={() => setFilters((s) => ({ ...s, tecnico_id: "" }))} className="text-primary/70 hover:text-primary" data-testid="rend-tecnico-filter-clear">✕</button>
              </div>
            )}
            {rendLoading && !rendData ? (
              <div className="space-y-5">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
                <Skeleton className="h-[320px] rounded-xl" />
              </div>
            ) : (() => {
              const rk = rendData?.kpi || {};
              const rc = rendData?.charts || {};
              const cmp = rendData?.confronto_periodo;
              const rv = cmp?.variazioni || {};
              const dK = (key, kind) => {
                const v = rv[key];
                if (!cmp || !v) return {};
                const { delta, delta_pct } = v;
                if (delta_pct === null) return { deltaText: "Nuovo valore", deltaTone: "new" };
                const abs = kind === "eur" ? `${delta > 0 ? "+" : ""}${eurK(delta)} €` : kind === "h" ? `${delta > 0 ? "+" : ""}${fmtOre(delta)} h` : `${delta > 0 ? "+" : ""}${delta}`;
                let tone = delta > 0 ? "pos" : delta < 0 ? "neg" : "neu";
                if (kind === "invh") tone = delta > 0 ? "amber" : delta < 0 ? "pos" : "neu";
                return { deltaText: `${abs} · ${delta_pct > 0 ? "+" : ""}${delta_pct}%`, deltaTone: tone };
              };
              return (
                <>
                  {cmp && (
                    <div className="text-xs text-muted-foreground" data-testid="rend-confronto-label">
                      Confronto <span className="font-medium text-foreground">{cmp.periodo_corrente.label}</span> vs periodo precedente <span className="font-medium text-foreground">{cmp.periodo_precedente.label}</span>
                    </div>
                  )}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <KpiStat label="Fatturato prodotto" value={euro(rk.fatturato_totale || 0)} icon={CurrencyEur} accent="text-primary" {...dK("fatturato_totale", "eur")} tip="Somma dell'importo finale delle sessioni rendicontate (logiche economiche validate)." />
                    <KpiStat label="Ore lavorate totali" value={fmtOre(rk.ore_lavorate_totali || 0)} icon={Clock} {...dK("ore_lavorate_totali", "h")} tip="Incl. Garanzia/Comm./Costo copia" />
                    <KpiStat label="Ore rendicontate" value={fmtOre(rk.ore_rendicontate || 0)} icon={Clock} accent="text-emerald-600" {...dK("ore_rendicontate", "h")} />
                    <KpiStat label="Ore non valorizzate" value={fmtOre(rk.ore_non_valorizzate || 0)} icon={Minus} {...dK("ore_non_valorizzate", "invh")} tip="Garanzia · Comm. · Costo copia" />
                  </div>
                  <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                    <KpiStat label="Media €/h reale" value={euro(rk.media_euro_ora_reale || 0)} icon={TrendUp} accent="text-primary" {...dK("media_euro_ora_reale", "eur")} tip="Fatturato prodotto / ore lavorate totali (include le ore a valore 0)." />
                    <KpiStat label="Media €/h su rendic." value={euro(rk.media_euro_ora_rendicontata || 0)} icon={TrendUp} {...dK("media_euro_ora_rendicontata", "eur")} tip="Fatturato prodotto / ore rendicontate." />
                    <KpiStat label="Media ore fatturate" value={`${fmtOre(rk.media_ore_fatturate || 0)} h`} icon={Clock} {...dK("media_ore_fatturate", "h")} tip="Ore lavorate totali / giorni con attività." />
                    <KpiStat label="Media fatt. giornaliera" value={euro(rk.media_fatturato_giornaliera || 0)} icon={CurrencyEur} {...dK("media_fatturato_giornaliera", "eur")} />
                    <KpiStat label="Giorni · Tecnici" value={`${rk.giorni_con_attivita || 0} · ${rk.tecnici_coinvolti || 0}`} icon={Users} {...dK("giorni_con_attivita", "n")} sub="Giorni · tecnici" />
                  </div>

                  <FatturatoPerTecnicoChart chart={rc.fatturato_per_tecnico} onTecnico={toggleTecnico} activeTecnicoName={activeTecnicoName} />
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <MediaOreFatturateChart chart={rc.media_ore_fatturate} onTecnico={toggleTecnico} activeTecnicoName={activeTecnicoName} />
                    <ChartCard title="Media fatturato oraria per tecnico (reale)" data={rc.media_oraria_per_tecnico || []} color="#7c3aed" money />
                  </div>
                  {cmp && <VariazioneFatturatoChart chart={rc.variazione_fatturato_per_tecnico} onTecnico={toggleTecnico} activeTecnicoName={activeTecnicoName} />}
                  <AndamentoOreLavorate data={{ charts: rc }} activeGran={granSel} onGran={setGranSel} onTecnico={toggleTecnico} activeTecnicoName={activeTecnicoName} />
                  <RiepilogoTecniciTable tecnici={rendData?.tecnici || []} />
                </>
              );
            })()}
          </div>
        </div>
      )}

      {tab === "clienti" && (
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-0">
          <aside className="w-full lg:flex-shrink-0" style={isDesktop ? { width: sidebarW } : undefined}>
            <Card className="rounded-xl border-slate-100 shadow-[0_2px_8px_-2px_rgba(0,57,106,0.08)] p-4 lg:sticky lg:top-16">
              <ReportFilters variant="sidebar" filters={filters} setF={setF} refs={refs} onSearch={onSearch} resetFilters={resetFilters} tab={tab} rowsLen={(clientiData?.clienti || []).length} exportExcel={exportExcel} exportReportCompleto={exportClienti} exporting={exporting} exportPdf={exportPdf} />
            </Card>
          </aside>
          <div className="hidden lg:flex items-stretch w-3 cursor-col-resize group flex-shrink-0" onMouseDown={startResize} title="Trascina per ridimensionare">
            <div className="w-0.5 mx-auto rounded-full bg-slate-200 group-hover:bg-primary transition-colors" />
          </div>
          <div className="flex-1 min-w-0 space-y-5">
            {/* Toolbar: soglia fatturato minimo + solo da verificare + badge filtro cliente */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Soglia fatturato min.</span>
              <div className="flex items-center gap-1 bg-secondary/60 rounded-sm p-0.5" data-testid="cli-soglia-selector">
                {[0, 1000, 5000, 10000].map((s) => (
                  <button key={s} onClick={() => setSogliaFatt(s)}
                    className={`px-2.5 py-1 rounded-sm text-xs font-medium transition-colors ${Number(sogliaFatt) === s ? "bg-white shadow-sm text-primary" : "text-muted-foreground hover:text-foreground"}`}
                    data-testid={`cli-soglia-${s}`}>{s === 0 ? "Tutti" : `${s / 1000}k €`}</button>
                ))}
              </div>
              <Input type="number" min="0" value={sogliaFatt} onChange={(e) => setSogliaFatt(Number(e.target.value) || 0)} className="rounded-sm h-8 w-28 text-xs" placeholder="Personalizzata €" data-testid="cli-soglia-custom" />
              <button
                type="button"
                onClick={() => setCliSoloVerif((v) => !v)}
                className={`inline-flex items-center gap-1.5 rounded-sm px-3 h-8 text-xs font-medium border transition-colors ${cliSoloVerif ? "bg-amber-50 border-amber-300 text-amber-700" : "border-border text-muted-foreground hover:text-foreground"}`}
                data-testid="cli-toggle-solo-da-verificare">
                <WarningCircle size={14} /> Solo da verificare
                {(clientiData?.kpi?.righe_da_verificare ?? 0) > 0 && <span className="ml-1 rounded-full bg-amber-200 text-amber-800 px-1.5 text-[10px]" data-testid="cli-conteggio-da-verificare">{clientiData.kpi.righe_da_verificare}</span>}
              </button>
            </div>
            {activeClienteName && (
              <div className="flex items-center gap-2 text-xs bg-primary/5 border border-primary/20 rounded-lg px-3 py-1.5 w-fit" data-testid="cli-cliente-filter-badge">
                <span className="text-primary font-medium">Filtro cliente: {activeClienteName}</span>
                <button onClick={() => setFilters((s) => ({ ...s, cliente_id: "" }))} className="text-primary/70 hover:text-primary" data-testid="cli-cliente-filter-clear">✕</button>
              </div>
            )}
            {clientiLoading && !clientiData ? (
              <div className="space-y-5">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
                <Skeleton className="h-[320px] rounded-xl" />
              </div>
            ) : (() => {
              const ck = clientiData?.kpi || {};
              const cc = clientiData?.charts || {};
              return (
                <>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <KpiStat label="Fatturato clienti" value={euro(ck.fatturato_totale || 0)} icon={CurrencyEur} accent="text-primary" tip="Somma dell'importo finale delle sessioni rendicontate (logiche economiche validate)." />
                    <KpiStat label="Ore lavorate totali" value={fmtOre(ck.ore_lavorate_totali || 0)} icon={Clock} tip="Incl. Garanzia/Comm./Costo copia" />
                    <KpiStat label="Ore rendicontate" value={fmtOre(ck.ore_rendicontate || 0)} icon={Clock} accent="text-emerald-600" />
                    <KpiStat label="Ore non valorizzate" value={fmtOre(ck.ore_non_valorizzate || 0)} icon={Minus} tip="Garanzia · Comm. · Costo copia" />
                  </div>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <KpiStat label="Media €/h reale" value={euro(ck.media_euro_ora_reale || 0)} icon={TrendUp} accent="text-primary" tip="Fatturato clienti / ore lavorate totali (include le ore a valore 0)." />
                    <KpiStat label="Clienti attivi" value={`${ck.clienti_attivi || 0}`} icon={Users} sub={ck.clienti_in_vista != null ? `${ck.clienti_in_vista} in vista` : undefined} />
                    <KpiStat label="Rapportini/interventi" value={`${ck.rapportini || 0}`} icon={IdentificationCard} />
                    <KpiStat label="Righe da verificare" value={`${ck.righe_da_verificare || 0}`} icon={WarningCircle} accent={ck.righe_da_verificare ? "text-amber-600" : undefined} />
                  </div>

                  <FatturatoPerClienteChart chart={cc.fatturato_per_cliente} onCliente={toggleCliente} activeClienteName={activeClienteName} />
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <OrePerClienteChart chart={cc.ore_per_cliente} onCliente={toggleCliente} activeClienteName={activeClienteName} />
                    <MediaOraClienteChart chart={cc.media_oraria_per_cliente} onCliente={toggleCliente} activeClienteName={activeClienteName} />
                  </div>
                  <ChartCard title="Fatturato per modalità di addebito" data={cc.fatturato_per_modalita || []} color="#0b8457" money />
                  <RiepilogoClientiTable clienti={clientiData?.clienti || []} onCliente={toggleCliente} onOpen={(id) => nav(`/clienti/${id}`)} />
                </>
              );
            })()}
          </div>
        </div>
      )}


      {tab === "ore-tecnici" && (
      <div className="space-y-3">
        {/* Toolbar: filtro rapido "Solo da verificare" + contatore */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setSoloDaVerificare((v) => !v)}
            aria-pressed={soloDaVerificare}
            data-testid="toggle-solo-da-verificare"
            className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-xs font-medium border transition-colors ${soloDaVerificare ? "bg-amber-500 border-amber-500 text-white" : "bg-white border-border text-muted-foreground hover:border-amber-400 hover:text-amber-700"}`}
          >
            <WarningCircle size={14} /> Solo da verificare
            <span className={`ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${soloDaVerificare ? "bg-white/25 text-white" : "bg-amber-100 text-amber-700"}`} data-testid="conteggio-da-verificare">{nDaVerificare}</span>
          </button>
          {nDaVerificare > 0 && !soloDaVerificare && (
            <span className="text-xs text-muted-foreground">{nDaVerificare} rapportin{nDaVerificare === 1 ? "o" : "i"} con valorizzazione da sistemare</span>
          )}
        </div>
      {/* Tabella */}
      <Card className="rounded-sm border-border shadow-none overflow-hidden" data-testid="report-table">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-secondary/40 text-muted-foreground border-b border-border">
              <tr>
                <SortH k="data">Data</SortH>
                <th className="px-3 py-2 text-left font-semibold">Numero</th>
                <SortH k="cliente_nome">Cliente</SortH>
                <SortH k="tecnico_nome">Tecnico</SortH>
                <th className="px-3 py-2 text-left font-semibold">Stato</th>
                <th className="px-3 py-2 text-left font-semibold">Modalità</th>
                <th className="px-3 py-2 text-left font-semibold">Contratto</th>
                <SortH k="ore" className="text-right">Ore</SortH>
                <th className="px-3 py-2 text-right font-semibold">Tariffa</th>
                <SortH k="importo_stimato" className="text-right">Importo</SortH>
                <th className="px-3 py-2 text-center font-semibold">Inviata</th>
                <th className="px-3 py-2 text-left font-semibold">Richiesta</th>
                <th className="px-3 py-2 text-center font-semibold">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr><td colSpan={13} className="px-4 py-10 text-center text-muted-foreground" data-testid="no-data">Nessun rapportino trovato per i filtri selezionati.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className={`border-b border-border/60 hover:bg-secondary/40 cursor-pointer ${r.da_verificare ? "bg-amber-50/60" : ""}`} onClick={() => nav(`/interventi/${r.id}`)} data-testid={`report-row-${r.id}`}>
                  <td className="px-3 py-2 whitespace-nowrap tabular-nums">{fmtD(r.data)}</td>
                  <td className="px-3 py-2 font-mono font-medium">{r.numero || "—"}</td>
                  <td className="px-3 py-2 max-w-[180px] truncate">{r.cliente_nome || "—"}</td>
                  <td className="px-3 py-2 max-w-[150px] truncate">{r.tecnico_nome || "—"}</td>
                  <td className="px-3 py-2"><span className={`inline-block px-1.5 py-0.5 rounded-sm text-[10px] font-medium ${STATO_COLOR[r.stato] || "bg-slate-100 text-slate-600"}`}>{r.stato_label}</span></td>
                  <td className="px-3 py-2"><span className="inline-block px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-blue-50 text-blue-700">{r.modalita_label}</span></td>
                  <td className="px-3 py-2 max-w-[140px] truncate text-muted-foreground">{r.contratto_nome || "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtOre(r.ore)}</td>
                  <td className="px-3 py-2 text-right tabular-nums" title={r.da_verificare && r.motivo_da_verificare ? r.motivo_da_verificare : (r.metodo_valorizzazione || "")}>
                    {r.tariffa == null ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-amber-100 text-amber-700" title={r.da_verificare && r.motivo_da_verificare ? r.motivo_da_verificare : ""}><WarningCircle size={11} /> {r.tariffa_mancante ? "Tariffa mancante" : (r.da_verificare ? "Da verificare" : "Mista")}</span> : euro(r.tariffa)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums" data-testid={`importo-${r.id}`} title={r.da_verificare && r.motivo_da_verificare ? r.motivo_da_verificare : (r.metodo_valorizzazione || "")}>
                    <div className="flex flex-col items-end">
                      <span className={r.override_attivo ? "font-semibold text-primary" : ""}>{euro(r.importo_stimato)}</span>
                      {r.override_attivo && (
                        <span className="text-[9px] px-1 py-px rounded-sm bg-primary/10 text-primary font-medium" title={`Calcolato: ${euro(r.importo_calcolato)}${r.override_motivo ? " · " + r.override_motivo : ""}`}>override</span>
                      )}
                      {!r.override_attivo && r.da_verificare && (
                        <span className="text-[9px] px-1 py-px rounded-sm bg-amber-100 text-amber-700 font-medium" data-testid={`da-verificare-${r.id}`} title={r.motivo_da_verificare || r.metodo_valorizzazione || ""}>da verificare</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-center">{r.inviata ? <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-700">Inviata</span> : <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-500">No</span>}</td>
                  <td className="px-3 py-2 max-w-[240px] truncate text-muted-foreground">{r.richiesta || "—"}</td>
                  <td className="px-3 py-2 text-center">
                    <button onClick={(e) => openOverride(e, r)} className="inline-flex items-center justify-center h-7 w-7 rounded-sm hover:bg-secondary text-muted-foreground hover:text-primary" title="Modifica importo (override)" data-testid={`override-btn-${r.id}`}>
                      <PencilSimple size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot className="bg-secondary/50 border-t-2 border-border font-semibold">
                <tr>
                  <td className="px-3 py-2.5" colSpan={7}>Totale ({rows.length} rapportini)</td>
                  <td className="px-3 py-2.5 text-right tabular-nums" data-testid="total-ore">{fmtOre(totOre)}</td>
                  <td className="px-3 py-2.5"></td>
                  <td className="px-3 py-2.5 text-right tabular-nums" data-testid="total-importo">{euro(totImp)}</td>
                  <td colSpan={3}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>
      </div>
      )}

      <Dialog open={!!ovrRow} onOpenChange={(o) => { if (!o) setOvrRow(null); }}>
        <DialogContent className="sm:max-w-md rounded-sm" data-testid="override-dialog">
          <DialogHeader>
            <DialogTitle>Override importo rapportino</DialogTitle>
            <DialogDescription>
              {ovrRow && <>Rapportino <span className="font-mono font-medium">{ovrRow.numero || "—"}</span> · {ovrRow.cliente_nome || "—"}</>}
            </DialogDescription>
          </DialogHeader>
          {ovrRow && (
            <div className="space-y-4">
              <div className="flex items-center justify-between text-sm bg-secondary/50 rounded-sm px-3 py-2">
                <span className="text-muted-foreground">Importo calcolato</span>
                <span className="tabular-nums font-medium">{euro(ovrRow.importo_calcolato || 0)}</span>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Importo finale (€)</Label>
                <Input type="number" step="0.01" min="0" value={ovrImporto} onChange={(e) => setOvrImporto(e.target.value)} className="mt-1 rounded-sm" data-testid="override-importo-input" autoFocus />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Motivo (opzionale)</Label>
                <Textarea value={ovrMotivo} onChange={(e) => setOvrMotivo(e.target.value)} rows={2} className="mt-1 rounded-sm" placeholder="Es. accordo commerciale, sconto concordato…" data-testid="override-motivo-input" />
              </div>
              {ovrRow.override_attivo && <p className="text-xs text-muted-foreground">Override attivo. Puoi aggiornarlo o rimuoverlo per tornare all'importo calcolato.</p>}
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            {ovrRow?.override_attivo && (
              <Button variant="outline" className="rounded-sm gap-1.5" onClick={removeOverride} disabled={ovrSaving} data-testid="override-remove-btn">
                <ArrowCounterClockwise size={15} /> Rimuovi override
              </Button>
            )}
            <Button className="rounded-sm" onClick={saveOverride} disabled={ovrSaving} data-testid="override-save-btn">Salva importo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CsatStars({ voto }) {
  const v = Math.round(Number(voto) || 0);
  return <span className="inline-flex gap-0.5">{[1, 2, 3, 4, 5].map((n) => <Star key={n} size={13} weight="fill" className={n <= v ? "text-amber-400" : "text-slate-200"} />)}</span>;
}

function CsatReport() {
  const [dataDa, setDataDa] = useState("");
  const [dataA, setDataA] = useState("");
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const loadR = useCallback(() => {
    setLoading(true);
    const params = {};
    if (dataDa) params.data_da = dataDa;
    if (dataA) params.data_a = dataA;
    api.get("/tickets/csat/report", { params }).then(({ data }) => setD(data)).catch(() => setD(null)).finally(() => setLoading(false));
  }, [dataDa, dataA]);
  useEffect(() => { loadR(); }, [loadR]);
  const [exportingPdf, setExportingPdf] = useState(false);
  const exportPdf = async () => {
    setExportingPdf(true);
    try {
      const params = {};
      if (dataDa) params.data_da = dataDa;
      if (dataA) params.data_a = dataA;
      const res = await api.get("/tickets/csat/report/pdf", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Soddisfazione_CSAT_${dataDa || "inizio"}_${dataA || "fine"}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Impossibile generare il PDF."); } finally { setExportingPdf(false); }
  };
  const dist = d?.distribuzione || {};
  const maxDist = Math.max(1, ...Object.values(dist).map((x) => Number(x) || 0));
  return (
    <div className="space-y-6" data-testid="csat-report">
      <div className="flex flex-wrap items-end gap-3">
        <div><Label className="text-xs">Da</Label><Input type="date" value={dataDa} onChange={(e) => setDataDa(e.target.value)} className="mt-1 rounded-sm h-9 w-44" data-testid="csat-report-da" /></div>
        <div><Label className="text-xs">A</Label><Input type="date" value={dataA} onChange={(e) => setDataA(e.target.value)} className="mt-1 rounded-sm h-9 w-44" data-testid="csat-report-a" /></div>
        {(dataDa || dataA) && <Button variant="outline" className="rounded-sm h-9" onClick={() => { setDataDa(""); setDataA(""); }} data-testid="csat-report-reset">Azzera</Button>}
        <Button variant="outline" className="rounded-sm h-9 gap-1.5" onClick={exportPdf} disabled={exportingPdf} data-testid="export-pdf"><DownloadSimple size={15} /> Esporta PDF</Button>
      </div>
      {loading ? <Skeleton className="h-40 w-full rounded-sm" /> : !d ? <p className="text-sm text-muted-foreground">Nessun dato disponibile.</p> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="rounded-sm border-border shadow-none p-4"><div className="text-xs text-muted-foreground">Sondaggi inviati</div><div className="text-2xl font-semibold mt-1" data-testid="csat-kpi-inviati">{d.inviati}</div></Card>
            <Card className="rounded-sm border-border shadow-none p-4"><div className="text-xs text-muted-foreground">Risposte ricevute</div><div className="text-2xl font-semibold mt-1" data-testid="csat-kpi-risposte">{d.risposte}</div></Card>
            <Card className="rounded-sm border-border shadow-none p-4"><div className="text-xs text-muted-foreground">Tasso di risposta</div><div className="text-2xl font-semibold mt-1" data-testid="csat-kpi-tasso">{d.tasso_risposta}%</div></Card>
            <Card className="rounded-sm border-border shadow-none p-4"><div className="text-xs text-muted-foreground">Voto medio</div><div className="text-2xl font-semibold mt-1 flex items-center gap-2" data-testid="csat-kpi-media">{d.media_voto || "—"}<CsatStars voto={d.media_voto} /></div></Card>
          </div>

          <Card className="rounded-sm border-border shadow-none p-4">
            <div className="text-sm font-semibold mb-3">Distribuzione voti</div>
            <div className="space-y-2">
              {[5, 4, 3, 2, 1].map((n) => (
                <div key={n} className="flex items-center gap-3">
                  <span className="w-16 text-xs flex items-center gap-1"><Star size={12} weight="fill" className="text-amber-400" /> {n}</span>
                  <div className="flex-1 bg-slate-100 rounded-sm h-4 overflow-hidden"><div className="bg-amber-400 h-full" style={{ width: `${((Number(dist[n]) || 0) / maxDist) * 100}%` }} /></div>
                  <span className="w-10 text-right text-xs text-muted-foreground">{dist[n] || 0}</span>
                </div>
              ))}
            </div>
          </Card>

          <Card className="rounded-sm border-border shadow-none overflow-hidden">
            <div className="px-4 py-3 border-b border-border text-sm font-semibold">Riepilogo per cliente</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border bg-secondary/40 text-left text-xs text-muted-foreground">
                  <th className="px-4 py-2 font-medium">Cliente</th><th className="px-4 py-2 font-medium text-center">Inviati</th><th className="px-4 py-2 font-medium text-center">Risposte</th><th className="px-4 py-2 font-medium text-center">Tasso</th><th className="px-4 py-2 font-medium">Voto medio</th>
                </tr></thead>
                <tbody>
                  {(d.per_cliente || []).length === 0 ? <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">Nessun dato nel periodo selezionato.</td></tr> :
                    (d.per_cliente || []).map((r, i) => (
                      <tr key={i} className="border-b border-border/60" data-testid={`csat-cliente-row-${i}`}>
                        <td className="px-4 py-2">{r.cliente_nome}</td>
                        <td className="px-4 py-2 text-center">{r.inviati}</td>
                        <td className="px-4 py-2 text-center">{r.risposte}</td>
                        <td className="px-4 py-2 text-center">{r.tasso}%</td>
                        <td className="px-4 py-2"><span className="flex items-center gap-2">{r.media || "—"} <CsatStars voto={r.media} /></span></td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card className="rounded-sm border-border shadow-none overflow-hidden">
            <div className="px-4 py-3 border-b border-border text-sm font-semibold">Risposte dei clienti</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border bg-secondary/40 text-left text-xs text-muted-foreground">
                  <th className="px-4 py-2 font-medium">Ticket</th><th className="px-4 py-2 font-medium">Cliente</th><th className="px-4 py-2 font-medium">Voto</th><th className="px-4 py-2 font-medium">Commento</th><th className="px-4 py-2 font-medium">Tecnico</th><th className="px-4 py-2 font-medium">Data</th>
                </tr></thead>
                <tbody>
                  {(d.dettaglio || []).length === 0 ? <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">Nessuna risposta ricevuta nel periodo.</td></tr> :
                    (d.dettaglio || []).map((r, i) => (
                      <tr key={i} className="border-b border-border/60" data-testid={`csat-detail-row-${i}`}>
                        <td className="px-4 py-2 font-mono text-xs">{r.numero_ticket}</td>
                        <td className="px-4 py-2">{r.cliente_nome}</td>
                        <td className="px-4 py-2"><span className="flex items-center gap-1.5">{r.voto} <CsatStars voto={r.voto} /></span></td>
                        <td className="px-4 py-2 max-w-xs text-slate-700">{r.commento || "—"}</td>
                        <td className="px-4 py-2">{r.assegnatario}</td>
                        <td className="px-4 py-2 text-xs text-muted-foreground">{fmtDate(r.submitted_at)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}


function LavorazioniReport() {
  const [dd, setDd] = useState("");
  const [da, setDa] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    api.get("/reportistica/lavorazioni", { params: { data_da: dd, data_a: da } })
      .then(({ data }) => setData(data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [dd, da]);
  useEffect(() => { load(); }, [load]);
  const [exportingPdf, setExportingPdf] = useState(false);
  const exportPdf = async () => {
    setExportingPdf(true);
    try {
      const res = await api.get("/reportistica/lavorazioni/pdf", { params: { data_da: dd, data_a: da }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Lavorazioni_${dd || "inizio"}_${da || "fine"}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Impossibile generare il PDF."); } finally { setExportingPdf(false); }
  };
  const nc = data?.non_completate, tot = data?.totale, comp = data?.completate;
  const Row = ({ r, highlight, bold }) => (
    <tr className={`border-b border-border/60 ${highlight ? "bg-emerald-50/50" : "hover:bg-secondary/30"} ${bold ? "font-semibold" : ""}`} data-testid={`lav-fase-row-${r.fase || "tot"}`}>
      <td className="px-3 py-2">
        <span className="inline-flex items-center gap-2">
          {r.color && <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: r.color }} />}
          {r.label}
        </span>
      </td>
      <td className="px-3 py-2 text-right tabular-nums">{r.count}</td>
      <td className="px-3 py-2 text-right tabular-nums">{euro(r.importo || 0)}</td>
      <td className="px-3 py-2 text-right tabular-nums">{fmtOre(r.ore_previste)} h</td>
      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{fmtOre(r.ore_consuntivate)} h</td>
    </tr>
  );
  return (
    <div className="space-y-6" data-testid="report-lavorazioni">
      <Card className="rounded-sm p-3 shadow-none">
        <div className="flex flex-wrap items-end gap-3">
          <div><Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Data conferma da</Label><Input type="date" value={dd} onChange={(e) => setDd(e.target.value)} className="mt-1 rounded-sm h-9" data-testid="lav-report-da" /></div>
          <div><Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Data conferma a</Label><Input type="date" value={da} onChange={(e) => setDa(e.target.value)} className="mt-1 rounded-sm h-9" data-testid="lav-report-a" /></div>
          {(dd || da) && <Button variant="outline" size="sm" className="rounded-sm h-9" onClick={() => { setDd(""); setDa(""); }} data-testid="lav-report-reset">Azzera periodo</Button>}
          <Button variant="outline" size="sm" className="rounded-sm h-9 gap-1.5" onClick={exportPdf} disabled={exportingPdf} data-testid="export-pdf"><DownloadSimple size={15} /> Esporta PDF</Button>
          <span className="text-xs text-muted-foreground ml-auto">{dd || da ? "Filtro su data conferma ordine" : "Tutte le lavorazioni"}</span>
        </div>
      </Card>

      {loading ? <Skeleton className="h-40 w-full rounded-sm" /> : !data ? <p className="text-sm text-muted-foreground">Nessun dato disponibile.</p> : (
        <>
          <div>
            <h3 className="font-heading text-sm font-semibold mb-2 flex items-center gap-1.5"><WarningCircle size={16} className="text-amber-500" weight="fill" /> Lavorazioni non completate</h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <KpiCard label="Lavorazioni non completate" value={nc.count} sub={`su ${tot.count} totali`} accent="text-amber-600" />
              <KpiCard label="Importo economico" value={euro(nc.importo)} sub="valore di vendita" />
              <KpiCard label="Ore previste" value={`${fmtOre(nc.ore_previste)} h`} sub="da pianificazione tecnici" />
              <KpiCard label="Ore allocate" value={`${fmtOre(nc.ore_consuntivate)} h`} sub="da interventi collegati" />
            </div>
          </div>

          <Card className="rounded-sm border-border shadow-none overflow-hidden">
            <h3 className="font-heading text-sm font-semibold flex items-center gap-1.5 p-4 pb-3"><ChartBar size={16} className="text-primary" /> Dettaglio per fase</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-secondary/50 text-muted-foreground border-y border-border">
                  <tr>
                    <th className="px-3 py-2 font-semibold text-left">Fase</th>
                    <th className="px-3 py-2 font-semibold text-right">Lavorazioni</th>
                    <th className="px-3 py-2 font-semibold text-right">Importo</th>
                    <th className="px-3 py-2 font-semibold text-right">Ore previste</th>
                    <th className="px-3 py-2 font-semibold text-right">Ore allocate</th>
                  </tr>
                </thead>
                <tbody>
                  {data.per_fase.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">Nessuna lavorazione nel periodo selezionato.</td></tr>}
                  {data.per_fase.map((r) => <Row key={r.fase} r={r} highlight={r.completata} />)}
                  <Row r={{ label: "Totale", ...tot }} bold />
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}


const TOPMENU = { assistenza: [
  { key: "ore-tecnici", label: "Ore Tecnici", icon: Clock, title: "Ore Tecnici", subtitle: "Elenco rapportini e ore tecniche" },
  { key: "report-ore", label: "Report Ore", icon: ChartLineUp, title: "Report Ore", subtitle: "Analisi ore, andamento e rendicontazione economica" },
  { key: "rendimento-tecnici", label: "Rendimento Tecnici", icon: Users, title: "Rendimento Tecnici", subtitle: "Rendimento dei tecnici: ore prodotte e fatturato generato" },
  { key: "clienti", label: "Clienti", icon: IdentificationCard, title: "Clienti", subtitle: "Rendimento dei clienti: fatturato prodotto e ore assorbite" },
  { key: "lavorazioni", label: "Lavorazioni", icon: ChartBar, title: "Lavorazioni", subtitle: "Importi economici, ore e lavorazioni ancora da completare" },
  { key: "soddisfazione", label: "Soddisfazione", icon: Smiley, title: "Soddisfazione clienti", subtitle: "Analisi delle valutazioni CSAT inviate alla chiusura dei ticket" },
] };

export default function Reportistica() {
  const [section] = useState("assistenza");
  const [tab, setTab] = useState("ore-tecnici");
  const current = TOPMENU[section].find((t) => t.key === tab) || TOPMENU[section][0];

  return (
    <InternalLayout>
      <div className="min-h-[calc(100vh-3.5rem)]">
        {/* Contenuto */}
        <div className="min-w-0 p-6 lg:p-8">
          <PageHeader title={current.title} subtitle={current.subtitle} />
          {/* Menu orizzontale */}
          <div className="flex items-center gap-1 border-b border-border mb-6" data-testid="reportistica-topmenu">
            {TOPMENU[section].map((t) => {
              const Icon = t.icon;
              return (
                <button key={t.key} onClick={() => setTab(t.key)} className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`} data-testid={`topmenu-${t.key}`}>
                  <Icon size={15} /> {t.label}
                </button>
              );
            })}
          </div>
          {tab === "soddisfazione" ? <CsatReport /> : tab === "lavorazioni" ? <LavorazioniReport /> : <OreTecnici tab={tab} />}
        </div>
      </div>
    </InternalLayout>
  );
}
